import { useEffect, useRef, useState } from 'react';
import {
  api, darf, datum, datumKurz, menge, navUrl, uhr, zeitpunkt,
  DOKUMENT_TEXT, GEWERK_TEXT, ZUSTAND_TEXT,
  type Anmelder, heuteIso} from '../api.ts';
import {
  Dialog, Fehlerkasten, Text, ausFehler, type Fehlerlage,
} from '../teile/Formular.tsx';
import { meinBereich, meineOrdner } from './Dateien.tsx';
import Auftragsinfoliste from '../teile/Auftragsinfoliste.tsx';
import { Dateikachel } from '../teile/Dateikachel.tsx';
import GbgsKarte from '../teile/GbgsKarte.tsx';
import Planungskarte from '../teile/Planungskarte.tsx';
import { useDateifenster } from '../teile/Dateifenster.tsx';
import { Klammer } from '../teile/Klammer.tsx';
import { useFrisch } from '../teile/Frisch.tsx';
import { Kopierbar } from '../teile/Kopierbar.tsx';
import { fragen } from '../teile/rueckfrage.tsx';

/* DIE ARTENTEXTE STEHEN IN `api.ts` UND NUR DORT. Hier stand eine Liste mit
   SECHS von achtzehn Werten - und eine halbierte Liste sieht aus wie eine
   ganze: was fehlt, steht roh da, und es fällt nur dem auf, der genau diese
   Art vor sich hat. In der Behördenmappe stand deshalb „GEBUEHRENBESCHEID". */
const DOK_TEXT = DOKUMENT_TEXT;
/** Die Wörter aus dem CHECK von `termin_verlauf.aktion`, für Menschen. */
const TERMIN_AKTION: Record<string, string> = {
  gebucht: 'gebucht',
  umgebucht: 'umgebucht',
  storniert: 'storniert',
  erledigt: 'fertig gemeldet',
  wieder_offen: 'wieder geöffnet',
};

/**
 * Wohin die Uebersicht springen kann.
 *
 * Die vier ersten sind Reiter derselben Akte, die zwei letzten Dialoge
 * darueber. Welches von beidem, entscheidet `Auftragsakte.tsx`: die Uebersicht
 * soll nicht wissen muessen, ob „Termin" ein Bildschirm oder ein Fenster ist.
 */
export type Sprungziel =
  'aufmass' | 'dateien' | 'behoerde' | 'leb' | 'termin' | 'vorbereiten'
  /* SEIT DEM 03.09.2026: „Kunde nicht erreicht" setzt oder beendet das
     Nachfassen. Er steht im Kundenblock, weil die Frage dort entsteht. */
  | 'nachfassen'
  /* SEIT DEM 18.09.2026: „Fertig melden" und „Baustelle fertig".
     ORIGINALTEXT MARVIN (18.09.2026)
       „du hast den neuenbereich eingebunden, dass die Partner den auftrag
        fertig melden können, und die unterlagen später hochladen. der ist nur
        am PC da. mobile fehlt er komplett. bitte darauf achten, dass beide
        ansichten immer alle funktionen haben"
     ES GAB KEINE ZWEITE ANSICHT UND KEINE CSS-REGEL, DIE ETWAS VERSTECKT
     HÄTTE. Beide Wege standen ausschliesslich im Klappmenü „Vorgang" ganz
     oben im Kopf der Akte - und der Kopf ist unter 700 Punkten `position:
     static`, rollt also mit weg. Wer in der Übersicht nach unten gerollt hat,
     hatte den einzigen Weg dorthin ausserhalb des Bildes. **Ein Weg, den man
     nicht findet, ist so gut wie keiner** - zum dritten Mal in diesem Haus,
     nach „Zurückmelden" und „Nicht erledigt", die aus genau diesem Grund am
     08.09.2026 Chips bekommen haben. */
  | 'fertig' | 'baustelle_fertig'
  /* SEIT DEM 31.08.2026. Marvin: „Wo hier diese Chips, diese Möglichkeit,
     direkt in den Block was zu editieren, aber fehlt, ist zum Beispiel beim
     Kunde, ist zum Beispiel bei der Technik ... und auch bei der Verwaltung."
     Beide Ziele gab es schon als Dialog über der Akte - sie waren nur aus den
     Blöcken heraus nicht erreichbar, in denen der Wert steht, den man ändern
     will. Der Weg führte über den Knopf „Bearbeiten" ganz oben und dann durch
     ein Formular mit allen Feldern des Auftrags. */
  /* UND SEIT DEM 31.08.2026 MIT BEREICH. Marvin: „hier sind zum bearbeiten
     verschiedene bereiche zusammengefasst bitte unterteile die zum
     bearbeiten/ändern in die einzelnen bereiche." Vorher führten alle drei
     Chips auf denselben Anfang derselben Rolle - ein Weg aus dem Kundenblock
     heraus, der beim Straßennamen landet, ist kein Weg in den Kundenblock. */
  | 'bearbeiten' | 'bearbeiten:kunde' | 'bearbeiten:netz'
  | 'bearbeiten:auftrag' | 'kommentar'
  /* UND SEIT DEM 08.09.2026 GILT DASSELBE FÜR DIE VORBEREITUNG. Marvin:
     „planung öffnen soll auf nur auf den bereich verweisen, nicht auf den
     gesamten popup «Auftrag vorbereiten»." Der Weg aus der Karte
     „Tiefbauaufwand" landete auf Seite 1, dem Lageplan für die Gemeinde -
     also zwei Seiten vor dem, was auf dem Chip steht. */
  | 'vorbereiten:planung'
  /* UND ZWEI WEGE VON DER BAUSTELLE ZURÜCK, BEIDE SEIT DEM 08.09.2026.
     Marvin: „hier geht es um die rückmeldungen die der tiefbau draußen macht
     ... außerdem braucht die montage neben dem fertig melden und dem
     einblasstop auch die möglichkeit über checkboxen bei «Auftrag nicht
     erledigt» zusammen zu klicken, was sache ist." Beide sind Fenster über
     der Akte, und beide gehen aus der Karte auf, in der die Angabe steht -
     nicht aus einem Klappmenü zwei Bildschirmecken weiter. */
  | 'technikmeldung' | 'nicht_erledigt'
  /* SEIT .502: der Hinweis auf neue WFMT-Einträge in der Übersicht öffnet den Verlauf. */
  | 'wfmt';

/**
 * Der Weg aus einer Kartenüberschrift heraus.
 *
 * Ein ECHTER `button`, kein anklickbarer `div`: er ist mit der Tastatur
 * erreichbar, und ein Vorleser nennt ihn beim Namen. Steht kein Weg zur
 * Verfügung - weil das Recht fehlt oder der Auftrag archiviert ist -, kommt
 * gar nichts. Ein toter Knopf sieht aus wie ein gesperrter, und ein Klick,
 * der nichts tut, kostet beim zweiten Mal das Vertrauen in alle übrigen.
 */
/** .498: eine Auswahl neben „Eintrag schreiben“; die gewählte Vorlage steht sofort in der Auftragsinfo. */
function AuftragsinfoVorlage({ auftragId, laden }: { auftragId: string; laden: () => Promise<void> }) {
  const [vorlagen, setVorlagen] = useState<any[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => { api.auftragsinfoVorlagen().then(setVorlagen).catch(() => setVorlagen([])); }, []);
  if (!vorlagen.length) return null;
  return (
    <span style={{ marginLeft: 8, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <select className="klein" value="" disabled={laeuft} aria-label="Vorlage in die Auftragsinfo schreiben"
              onChange={async (e) => {
                const v = vorlagen.find((x) => x.id === e.target.value); e.target.value = '';
                if (!v) return;
                setLaeuft(true); setFehler(null);
                try { await api.auftragKommentar(auftragId, v.text); await laden(); }
                catch (f: any) { setFehler(f?.message ?? 'Das hat nicht geklappt.'); }
                finally { setLaeuft(false); }
              }}>
        <option value="">{laeuft ? 'Einen Moment' : 'Vorlage …'}</option>
        {vorlagen.map((v) => <option key={v.id} value={v.id} title={v.text}>{v.titel}</option>)}
      </select>
      {fehler && <span className="klein fehlertext">{fehler}</span>}
    </span>
  );
}

function Kartenweg(
  { hin, text, fertig }:
  { hin: (() => void) | null; text: string; fertig?: boolean }
) {
  if (!hin) return null;
  /* KEIN PFEIL MEHR, UND SICHTBAR OHNE MAUS.
   *
   * ORIGINALTEXT MARVIN (31.08.2026)
   *   „Ich würde das noch ein bisschen markanter machen, vielleicht direkt als
   *    Chip hinterlegen, nicht erst, wenn ich mit der Maus drüber fahre und
   *    auch die Pfeile dahinter sozusagen wegmachen."
   *
   * Der Rand war `transparent` und der Untergrund kam erst beim Überfahren -
   * auf einem Tablet gibt es kein Überfahren, und am Schreibtisch findet man
   * nur, wonach man schon sucht. Ein Weg, den man erst sieht, wenn man ihn
   * gefunden hat, ist keiner. Das Aussehen steht in `.kartenweg`.
   *
   * Der Pfeil ist weg: er sagte „es geht weiter", und das sagt der Chip
   * selbst. */
  /* UND WENN ES SCHON GETAN IST, SIEHT MAN ES AM CHIP.
   *
   * ORIGINALTEXT MARVIN (08.09.2026)
   *   „Außerdem oben bei dem Block Baustelle, wo jetzt Vorbereiten steht.
   *    Wenn die Vorbereitungen getroffen sind, dann soll der Chip
   *    entsprechend grün werden und von Vorbereiten der Text geändert werden
   *    auf bereits vorbereitet."
   *
   * Ein erledigter Schritt ist eine GUTE Nachricht und darf so aussehen -
   * dieselbe Entscheidung wie beim erledigten Gewerk (31.08.2026) und bei der
   * erzeugten Mappe (01.09.2026). Der Weg BLEIBT: wer nachsehen oder etwas
   * ändern will, klickt weiter darauf. */
  return (
    <button type="button"
            className={'kartenweg' + (fertig ? ' fertig' : '')}
            onClick={hin}>
      {text}
    </button>
  );
}

const BEREICH_TEXT: Record<string, string> = {
  oeffentlich: 'öffentlichen Grund',
  privat: 'privaten Grund',
  sammel_vao: 'Bereich einer Sammel-VAO',
};

/**
 * Was die Bauakte an dieser Stelle sagte, wenn heute etwas anderes dasteht.
 *
 * ORIGINALTEXT MARVIN (25.08.2026)
 *   „und was ist, wenn es geändert wurde, dann muss das bei uns ja auch
 *    angezeigt werden, ergänzend zum original aus der bauakte."
 *
 * DIE ENTSCHEIDUNG, OB ES ETWAS ZU ZEIGEN GIBT, FÄLLT IN DER DATENSCHICHT.
 * `v_bauakte_wert` liefert nur die Felder, deren heutiger Wert ein anderer
 * ist; steht hier nichts, gibt es nichts zu erklären. Diese Zeile ist keine
 * Abweichung zum Abhaken: eine Röhrchenfarbe darf sich auf der Baustelle
 * ändern. Sie ist die Antwort auf „warum steht da etwas anderes als im
 * Papier".
 */
function Bauaktenwert(
  { werte, feld }: { werte: any[] | undefined; feld: string }
) {
  const w = (werte ?? []).find((x: any) => x.feld === feld);
  if (!w) return null;
  return (
    <div className="bauaktenwert" title={w.dateiname ?? undefined}>
      Bauakte sagte: <b>{w.wert}</b>
      {w.gelesen_am && <span className="leise">, {datum(w.gelesen_am)}</span>}
    </div>
  );
}

/* WAS DER TIEFBAU VOR ORT VORGEFUNDEN HAT.
 *
 * ORIGINALTEXT MARVIN (08.09.2026)
 *   „bei allen aufträgen in der technik kommen diese kommentare weg. da muss
 *    nur die info, wie darüber in blau hin, wenn was geändert wurde durch den
 *    TB! ansonsten sind diese infotexte blödsinn."
 *
 * ER HAT ZWEIMAL RECHT, UND DER ZWEITE TEIL IST DER BAU. Unter jedem der vier
 * Felder stand „Weicht es vor Ort ab: Karte «Tiefbau» → «Zurückmelden»" - bei
 * jedem Auftrag, an jedem Feld, jeden Tag. **Ein Satz, der immer dasteht, wird
 * auch an dem Tag nicht gelesen, an dem er zutrifft**; vier davon
 * untereinander sind eine Wand, hinter der die eine Zeile verschwindet, auf
 * die es ankommt.
 *
 * Was der Tiefbau WIRKLICH gemeldet hat, bekommt dieselbe Gestalt wie die
 * Bauakte darüber: eine abgesetzte Zeile mit dem Wert und seinem STEMPEL. Sie
 * steht nur da, wenn es sie gibt - und dann sagt sie alles, was der Hinweis
 * erklären wollte, an der Stelle, an der es zählt. */
function Istwert(
  { wert, am, name }:
  { wert: string | null | undefined; am?: string | null; name?: string | null }
) {
  if (!wert) return null;
  return (
    <div className="bauaktenwert">
      Vor Ort: <b>{wert}</b>
      {(name || am) && (
        <span className="leise">
          {', '}zurückgemeldet{name ? ` von ${name}` : ''}
          {am ? `, ${datum(am)}` : ''}
        </span>
      )}
    </div>
  );
}

const ZUSTAND_DOK: Record<string, string> = {
  entwurf: 'Entwurf',
  erstellt: 'erstellt',
  im_ordner: 'liegt im Ordner',
  versendet: 'versendet',
  ueber_portal_eingestellt: 'über Portal eingestellt',
  ersetzt: 'ersetzt',
};

/**
 * Die Uebersicht einer Akte: Gewerke, Aufmass, Verlauf, Baustelle, Papiere.
 *
 * Lesend. Geaendert wird in den eigenen Reitern und in den Dialogen, die
 * Auftragsakte.tsx darueberlegt.
 */
export default function AkteUebersicht(
  { ich, d, auftragId, laden, springe }:
  {
    ich: Anmelder; d: any; auftragId: string; laden: () => Promise<void>;
    /** Wohin ein Klick in dieser Uebersicht fuehrt: Reiter oder Dialog. */
    springe: (ziel: Sprungziel) => void;
  }
) {
  const k = d.kopf;
  const darfErinnern = darf(ich, 'erinnerung.pflegen');
  /* DAS FENSTER FÜR EINE DATEI. Der Haken steht am ANFANG und erreicht jeden
     Durchgang - einer hinter einer bedingten Rückgabe ist auf dem Telefon ein
     weisser Bildschirm (React-Fehler 310). */
  const { fenster: dateifenster, zeige: zeigeDatei } = useDateifenster();
  // WOHIN EIN KLICK FUEHRT, ENTSCHEIDET DAS RECHT. Eine Zeile, die sich wie
  // ein Knopf anfuehlt und dann nichts tut, ist schlimmer als eine, die
  // stillsteht: der erste tote Klick kostet zwei Sekunden, der zweite das
  // Vertrauen in alle uebrigen.
  const zumTermin = darf(ich, 'termin.vergeben') && !k.archiviert_am
    ? () => springe('termin') : null;
  const zumAufmass = darf(ich, 'auftrag.lesen')
    ? () => springe('aufmass') : null;
  const zurBehoerde = darf(ich, 'dokument.erzeugen')
    ? () => springe('behoerde') : null;
  const zumVorbereiten = darf(ich, 'vorbereiten.pflegen') && !k.archiviert_am
    ? () => springe('vorbereiten') : null;
  /* UND DER WEG IN DIE PLANUNG FÜHRT AUF DIE PLANUNG.
     Marvin, 08.09.2026: „planung öffnen soll auf nur auf den bereich
     verweisen, nicht auf den gesamten popup «Auftrag vorbereiten»."
     Er hängt an demselben Recht wie `zumVorbereiten` - es ist dasselbe
     Fenster, nur eine andere Seite darin. Bis .137 stand hier gar keine
     Prüfung: der Chip erschien auch dem, den der Server danach abweist. */
  const zurPlanung = darf(ich, 'vorbereiten.pflegen') && !k.archiviert_am
    ? () => springe('vorbereiten:planung') : null;
  /* DIE ZWEI WEGE VON DER BAUSTELLE ZURÜCK.
     `akte.technik_melden` ist ein EIGENES Recht (Migration 188): wer die
     Technik LESEN darf, darf sie deshalb noch lange nicht berichtigen - und
     wer draussen steht, braucht dafür kein Schreibrecht auf den ganzen
     Auftrag. Die zweite Grenze - meldet diese Firma an DIESEM Tiefbau
     zurück - zieht der Server; ein Chip, der auf ein 403 führt, wäre
     schlimmer als kein Chip, deshalb sagt das Fenster den Grund, statt einen
     grauen Knopf zu zeigen.
     „Nicht erledigt" hängt an denselben zwei Rechten wie „Fertig melden" und
     der Einblasstop: es ist die dritte Antwort auf dieselbe Frage - was ist
     mit diesem Auftrag geschehen. */
  const zurTechnikmeldung = darf(ich, 'akte.technik_melden')
    && !k.archiviert_am ? () => springe('technikmeldung') : null;
  const zumNichtErledigt =
    (darf(ich, 'aufmass.erfassen') || darf(ich, 'aufmass.pruefen'))
    && !k.archiviert_am ? () => springe('nicht_erledigt') : null;
  /* DIESELBEN ZWEI RECHTE WIE DIE BEIDEN DARÜBER, denn es ist dieselbe Frage:
     was ist mit diesem Auftrag draussen geschehen. „Baustelle fertig" heisst
     „draussen ist nichts mehr zu tun, die Unterlagen kommen nach" und setzt
     keinen Haken; „Fertig melden" hakt die Gewerke ab. Beide gehören in den
     Block, in dem der Stand steht, und nicht nur in ein Klappmenü im Kopf. */
  const zumFertigMelden =
    (darf(ich, 'aufmass.erfassen') || darf(ich, 'aufmass.pruefen'))
    && !k.archiviert_am ? () => springe('fertig') : null;
  const zurBaustelleFertig =
    (darf(ich, 'aufmass.erfassen') || darf(ich, 'aufmass.pruefen'))
    && !k.archiviert_am ? () => springe('baustelle_fertig') : null;
  /* DIE ZWEI WEGE, DIE IN DEN BLÖCKEN GEFEHLT HABEN.
     `zumBearbeiten` öffnet dasselbe Formular wie der Knopf oben - aus dem
     Block heraus, in dem der Wert steht. `zumEintrag` schreibt in die
     Auftragsinfo. Beide hängen am Recht und am Archiv: ein archivierter
     Auftrag wird nicht mehr geändert. */
  const zumBearbeiten = darf(ich, 'auftrag.schreiben') && !k.archiviert_am
    ? () => springe('bearbeiten') : null;
  /* JEDER CHIP NENNT SEINEN BEREICH. Die Übersicht entscheidet damit nicht,
     WIE das Fenster gebaut ist - sie sagt nur, worum es geht. */
  const zumBereich = (b: 'kunde' | 'netz' | 'auftrag') =>
    darf(ich, 'auftrag.schreiben') && !k.archiviert_am
      ? () => springe(('bearbeiten:' + b) as Sprungziel) : null;
  /* UND DER CHIP HÄNGT AM SCHREIBRECHT, NICHT AM LESERECHT.
     Hier stand `auftrag.lesen` - das hat jeder, auch der Monteur ohne
     `akte.rueckmeldung`. Der Server weist dessen Eintrag ab, und ein Chip,
     der auf ein 403 führt, ist schlimmer als kein Chip: man drückt ihn,
     bekommt eine rote Meldung und glaubt der Oberfläche danach nicht mehr.
     Es sind dieselben zwei Rechte, an denen das Fenster hängt. */
  const zumEintrag =
    (darf(ich, 'auftrag.schreiben') || darf(ich, 'akte.rueckmeldung'))
    && !k.archiviert_am
      ? () => springe('kommentar') : null;

  /* ============================================================
   * IST DIE VORBEREITUNG SCHON GETROFFEN?
   * ============================================================
   * ORIGINALTEXT MARVIN (08.09.2026)
   *   „Außerdem oben bei dem Block Baustelle, wo jetzt Vorbereiten steht.
   *    Wenn die Vorbereitungen getroffen sind, dann soll der Chip
   *    entsprechend grün werden und von Vorbereiten der Text geändert werden
   *    auf bereits vorbereitet."
   *
   * **GEFRAGT WIRD DER BILDSCHIRM, DER DIE ANTWORT OHNEHIN GIBT.** Der Stand
   * einer Vorbereitung ist keine Spalte, sondern eine Rechnung aus fünf
   * Angaben (fehlende Pflichtfelder, Lageplan, Empfängeradresse der Gemeinde,
   * der bei der Terminvergabe gewählte Bereich, die freiwillige Schätzung),
   * und sie steht EINMAL in `regeln/vorbereitung.ts`. Wer sie hier nachbaut
   * oder ihr Ergebnis in `GET /auftraege/:id` ein zweites Mal ausrechnet, hat
   * zwei Wahrheiten - und dann sagt der Chip etwas anderes als das Fenster,
   * das er öffnet.
   *
   * ENTSCHIEDEN WIRD AN DER FARBE UND NICHT AN `ok`. `ok` ist auch wahr,
   * solange der Lageplan noch geprüft wird (`farbe: 'neutral'`); grün wird
   * der Chip erst, wenn das Fenster daneben grün sagt. **Ein Chip, der
   * „fertig" sagt, während der Bildschirm dahinter noch prüft, ist eine
   * Zusage, die niemand gegeben hat.**
   *
   * GEHOLT WIRD NUR, WER DEN WEG AUCH GEHEN DARF. Ohne
   * `vorbereiten.pflegen` steht der Chip gar nicht da, und ein Abruf für
   * einen Chip, den niemand sieht, ist eine Anfrage zu viel. Ein
   * fehlgeschlagener Abruf hält NICHTS auf: dann steht dort „Vorbereiten"
   * wie bisher - der Chip ist eine Auskunft und kein Riegel. */
  const [vorbereitungFertig, setVorbereitungFertig] = useState(false);
  /* Die Planungskarte zum Ansehen (.455), für uns und für die Partner. */
  const [planungskarteOffen, setPlanungskarteOffen] = useState(false);
  /* WELCHE AKTE GERADE GEMEINT IST. Wer schnell von einer zur nächsten
     klickt, bekäme sonst die Antwort der vorigen - und der Chip stünde grün
     über einem Auftrag, den niemand vorbereitet hat. */
  const gemeint = useRef(auftragId);
  gemeint.current = auftragId;
  /**
   * DER EIGENE ABRUF DES CHIPS, ALS FUNKTION UND NICHT IM EFFEKT.
   *
   * Er stand im Effekt und hing an `[auftragId, k.archiviert_am]`. Beide
   * ändern sich beim Schliessen des Vorbereiten-Fensters NICHT: die Akte zog
   * nach, der Chip blieb auf „Vorbereiten" stehen, und genau das hat Marvin
   * am 09.09.2026 beanstandet. Als Funktion daneben kann `useFrisch` sie
   * nachziehen - **eine Angabe, die ihren eigenen Abruf hat, braucht ihren
   * eigenen Nachzug.**
   */
  async function ladeVorbereitung() {
    if (!(darf(ich, 'vorbereiten.pflegen') && !k.archiviert_am)) {
      setVorbereitungFertig(false);
      return;
    }
    const fuer = auftragId;
    try {
      const v = await api.vorbereiten(fuer);
      if (gemeint.current !== fuer) return;
      setVorbereitungFertig(v?.stand?.farbe === 'gruen');
    } catch { /* der Chip ist eine Auskunft und kein Riegel */ }
  }
  useEffect(() => { ladeVorbereitung(); }, [auftragId, k.archiviert_am]);
  useFrisch(ladeVorbereitung);

  /* EINEN EINTRAG BERICHTIGEN.
   *
   * ORIGINALTEXT MARVIN (31.08.2026)
   *   „bei der sauftragsinfo kann ich nur einen eintrag schreiben, aber nichts
   *    editieren."
   *
   * NUR WAS EIN MENSCH GESCHRIEBEN HAT. Eine Systemmeldung („Termin umgebucht
   * auf 15.08.") beschreibt, was wirklich geschehen ist; der Server weist sie
   * ab, und der Knopf steht deshalb gar nicht erst daneben - ein Knopf, der
   * auf ein 409 führt, ist ein toter Knopf. */
  const [berichtigen, setBerichtigen] = useState<any>(null);
  const berichtige = (e: any) => setBerichtigen(e);

  /* WER DARF DIESEN EINEN EINTRAG ANFASSEN?
   *
   * ORIGINALTEXT MARVIN (01.09.2026)
   *   „auch soll die möglichkeit sein, jeder seinen eigenen eintrag zu löschen
   *    und der innendienst alle zu löschen und bearbeiten zu können"
   *
   * DIE ANZEIGE RECHNET DIESELBE GRENZE WIE DER SERVER, und das ist hier
   * ausnahmsweise kein Nachbau, sondern die Bedingung für einen ehrlichen
   * Knopf: auf Marvins Bildschirmfoto vom 01.09.2026 stand neben JEDEM der
   * drei Einträge „berichtigen" - auch neben denen von Marvin und Nicole -,
   * obwohl der angemeldete Partner sie gar nicht ändern darf. **Ein Knopf,
   * der auf ein 403 führt, ist schlimmer als kein Knopf:** man drückt ihn,
   * bekommt eine rote Meldung und glaubt der Oberfläche danach nicht mehr.
   *
   * Der Riegel bleibt trotzdem im SERVER - er ist die Wahrheit, die Anzeige
   * ist die Höflichkeit. */
  const darfEintrag = (e: any) =>
    e.quelle === 'person' && !k.archiviert_am
    && (darf(ich, 'auftrag.schreiben')
        || (!!e.benutzer_id && e.benutzer_id === ich.benutzerId));

  /* DER KURZE NAME WIRD IN DER LISTE SELBST GERECHNET
     (`teile/Auftragsinfoliste`). Er hing an DIESEM Bildschirm und galt
     deshalb nur hier; im Wochenplan stand der volle Name. Gerechnet wird er
     über die ganze Liste - ob ein Vorname ausreicht, hängt daran, wer sonst
     noch darin steht -, also gehört er dorthin, wo die Liste steht. */

  const [loeschen, setLoeschen] = useState<any>(null);
  const abschnittName = (aid: string | null) => {
    if (!aid) return null;
    const a = d.abschnitte.find((x: any) => x.id === aid);
    return a
      ? `${a.typ === 'grube' ? 'Grube' : 'Graben'} ${a.reihenfolge}${a.bezeichnung ? ' ' + a.bezeichnung : ''}`
      : null;
  };

  return (
    <>
      <div>
        <div className="nebeneinander breitschmal">
          <div>
            {/* ------------------------------------- Was noch fehlt (Sub) */}
            {ich.istSub && (
              <WasFehlt auftragId={auftragId} gewerke={d.gewerke}
                        zumUpload={darf(ich, 'datei.hochladen')
                                   ? () => springe('dateien') : null}
                        /* UND DER WEG INS AUFMASS.
                           ORIGINALTEXT MARVIN (03.09.2026)
                             „wo habe ich bei dem monteur nun die
                              aufmaßmöglichkeit? die fehlt ja noch komplett"

                           DEN REITER GAB ES, mit „Position hinzufügen" und
                           „Absenden" - er hiess nur „Aufmaß" und stand in
                           einer Reiterleiste, die auf dem Telefon rollt.
                           **Ein Weg, den man nicht findet, ist für den, der
                           ihn sucht, kein Weg** - dieselbe Lehre wie beim
                           Upload zwei Tage vorher. */
                        zumAufmass={darf(ich, 'aufmass.erfassen')
                                    ? () => springe('aufmass') : null} />
            )}

            {/* ------------------------------ Neue WFMT-Einträge (.502) */}
            {/* Marvin: „wenn die bei uns in der Liste sind und dann ein neuer
                Eintrag kommt, dass man dann eine Info bekommt und sieht. Am
                besten dann natürlich irgendwo in der Übersicht." Kein Chip in
                der Liste; hier steht es, und ein Klick öffnet den Verlauf. */}
            {Number(k.wfmt_ungelesen) > 0 && !ich.istSub && (
              <div className="hinweis warnung" style={{ cursor: 'pointer' }} role="button"
                   onClick={() => springe('wfmt')}
                   title="Öffnet den WFMT-Verlauf; damit gilt er als gelesen.">
                <b>WFMT-Verlauf: {k.wfmt_ungelesen} neue{Number(k.wfmt_ungelesen) === 1 ? 'r Eintrag' : ' Einträge'} der Telekom</b>
                {k.wfmt_geaendert_am ? `Eingelesen am ${new Date(k.wfmt_geaendert_am).toLocaleString('de-DE', { timeZone: 'Europe/Berlin', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} Uhr. ` : ''}Zum Lesen anklicken.
              </div>
            )}

            {/* ------------------------------ Behördenpost hängt (.466) */}
            {d.behoerdenpost_haengt && <BehoerdenpostHaengt x={d.behoerdenpost_haengt} auftragId={auftragId} laden={laden} />}

            {/* ------------------------------------ Was der Tiefbau hinterliess */}
            <Tiefbaustand x={d.tiefbaustand} melden={zurTechnikmeldung}
                          nichtErledigt={zumNichtErledigt}
                          fertigMelden={zumFertigMelden}
                          baustelleFertig={zurBaustelleFertig} />

            {/* ---------------------------------------------- Gewerke
                Der Terminblock. `akte.termin` entscheidet, ob er kommt: fuer
                den Monteur ist es die Antwort auf „wann bin ich dran", fuer
                eine Rolle ohne Bauzuordnung ist er Laerm. */}
            {d.bloecke?.termin !== false && (
            <div className="karte">
              <h3>
                Gewerke
                <Kartenweg hin={zumTermin} text="Termin vergeben" />
              </h3>
              <div className="leib">
                {/* ------------------------------ DER HINWEIS IST WEG
                  *
                  * ORIGINALTEXT MARVIN (22.09.2026, mit dem Bildschirmfoto,
                  * auf dem 3 gelbe Kästen übereinander stehen)
                  *   „Und den hinweis zu dem Termin kannst du komplett
                  *    entfernen."
                  *
                  * Hier standen die Kästen „Tiefbau am 15.09.2026 steht
                  * nicht im Terminplan. Der Termin liegt vor dem 22.09.2026."
                  * Gebaut am 09.09.2026, als EIN Auftrag im Plan fehlte und
                  * niemand sagen konnte, an welcher der 6 Bedingungen es lag.
                  *
                  * **DIE FRAGE WAR BEANTWORTET, DER KASTEN BLIEB.** Auf
                  * Marvins Bild stehen 3 davon übereinander und sagen alle
                  * dasselbe: der Termin liegt in der Vergangenheit, der Plan
                  * zeigt ab heute. Das ist keine Auskunft über einen Fehler,
                  * das ist die Bauweise des Wochenplans - und ein Kasten, der
                  * bei jedem älteren Auftrag erscheint, verdeckt genau die
                  * Kästen, die etwas zu sagen haben.
                  *
                  * DIE RECHNUNG DAHINTER BLEIBT. `regeln/terminbefund.ts`
                  * und `GET /auftraege/:id/terminbefund` stehen weiter und
                  * sind die Antwort auf „warum fehlt dieser Auftrag im Plan"
                  * - nur eben auf Nachfrage und nicht in jeder Akte. */}
                <table className="liste">
                  <thead>
                    <tr>
                      <th>Gewerk</th>
                      <th>Firma</th>
                      <th style={{ width: 130 }}>Termin</th>
                      <th style={{ width: 130 }}>Stand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.gewerke.map((g: any) => {
                      // Ein Gewerk, das nach dem Auftragstyp nicht unseres
                      // ist, wird nicht als offen gefuehrt. Sonst steht bei
                      // einem reinen Montageauftrag ewig „Tiefbau: offen" fuer
                      // einen Tiefbau, den wir nie machen werden.
                      const unser = {
                        TB: k.gewerke_soll?.tiefbau !== false,
                        EB: k.gewerke_soll?.einblasen !== false,
                        SP: k.gewerke_soll?.spleissen !== false,
                      }[g.art as 'TB' | 'EB' | 'SP'] ?? true;
                      return (
                      <tr key={g.id}
                          style={{ cursor: zumTermin ? 'pointer' : 'default' }}
                          title={zumTermin
                            ? `Termin für ${GEWERK_TEXT[g.art]} vergeben oder umbuchen`
                            : undefined}
                          onClick={zumTermin ?? undefined}>
                        <td>
                          <b>{GEWERK_TEXT[g.art]}</b>
                          {g.fenster === 'ausnahme' && (
                            <span className="leise klein">, Randzeit</span>
                          )}
                          {!unser && (
                            <div className="leise klein">nicht unser Gewerk</div>
                          )}
                          {/* WAS DER TIEFBAU HINTERLASSEN HAT, STEHT AN
                              SEINER ZEILE. Marvin, 31.08.2026: „das ist was,
                              was man unter ‚Gewerke' mit reinbringen kann bzw
                              auch im bereich ‚technik' duplizieren." Beides,
                              und mit Absicht: hier liest es der, der den
                              Termin ansieht, dort der, der die Technik
                              nachschlägt. */}
                          {/* HIER STAND DIE ZAHL AUS DER BAUAKTE UNTER DER
                              AUFSCHRIFT „VERLEGT".
                              ORIGINALTEXT MARVIN (02.09.2026)
                                „hier ist wieder der unterschied der
                                 Pipelänge auf der seite mehrmals drin, oben
                                 sagt der Tb er hat 6m verlegt, danach bei
                                 gewerke und bei technik nimmst du aber die
                                 werte aus der bauakte."
                              Er hat recht, und es war dieselbe Aufschrift,
                              die am 01.09.2026 schon einmal fiel - nur eine
                              Karte weiter. **Was der Tiefbau VERLEGT hat,
                              steht in seinem Aufmaß und nirgends sonst.**
                              `snr_laenge_m` ist die geplante Länge vom
                              Deckblatt der Bauakte; sie steht im Block
                              „Technik" mit ihrer Herkunft und hat an der
                              Gewerkezeile nichts zu suchen. Ist noch nichts
                              aufgemessen, steht hier NICHTS - eine geplante
                              Zahl an dieser Stelle ist eine Behauptung über
                              die Baustelle. */}
                          {/* UND ES IST DER GRABEN UND NICHT „DIE METER".
                              An der Zeile des TIEFBAUS steht, was der Tiefbau
                              gegraben hat (10030300). Die Pipe wird
                              eingeblasen und gehört der Montage; sie steht in
                              der Tiefbaukarte neben der Planangabe, unter
                              ihrem eigenen Namen. **Eine Zahl ohne den Namen
                              ihrer Position beantwortet beide Fragen und
                              damit keine.** */}
                          {g.art === 'TB'
                            && (d.tiefbaustand?.graben_aufgemessen != null
                                || k.snr_rohrverband_ist || k.snr_roehrchen_ist) && (
                            <div className="leise klein" style={{ marginTop: 2 }}>
                              {d.tiefbaustand?.graben_aufgemessen != null
                                && `${menge(d.tiefbaustand.graben_aufgemessen)} m Graben aufgemessen`}
                              {(k.snr_rohrverband_ist || k.snr_roehrchen_ist) && (
                                <>, vor Ort {[k.snr_rohrverband_ist,
                                  k.snr_roehrchen_ist].filter(Boolean).join(' / ')}</>
                              )}
                            </div>
                          )}
                        </td>
                        <td>
                          {/* VERGEBEN, ABER NICHT AN SIE (23.09.2026). Eine
                              Partnerfirma sieht den Namen der anderen nicht;
                              „nicht vergeben" wäre dann eine falsche Auskunft. */}
                          {g.firma ?? <span className="leise">
                            {g.vergeben ? 'andere Partnerfirma' : 'nicht vergeben'}</span>}
                        </td>
                        <td className="klein">
                          {g.termin_datum ? (
                            <>
                              {datum(g.termin_datum)}
                              <div className="leise">
                                {uhr(g.termin_von)}
                                {g.termin_bis ? ` bis ${uhr(g.termin_bis)}` : ''}
                              </div>
                            </>
                          ) : (
                            <span className="leise">kein Termin</span>
                          )}
                        </td>
                        <td className="klein">
                          {g.erledigt ? (
                            <span className="marke-zustand z-erledigt">
                              erledigt {datum(g.erledigt_am)}
                            </span>
                          ) : unser ? (
                            <span className="leise">offen</span>
                          ) : (
                            <span className="leise">entfällt</span>
                          )}
                          {g.restarbeiten_offen && (
                            <div style={{ color: 'var(--ueberfaellig)', marginTop: 3 }}>
                              Restarbeiten offen
                            </div>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>

                {d.gewerke.some((g: any) => g.kommentare?.length) && (
                  <div style={{ marginTop: 14 }}>
                    {d.gewerke.map((g: any) =>
                      (g.kommentare ?? []).map((k2: any, i: number) => (
                        <div key={g.id + i} style={{ marginBottom: 8 }}>
                          <div className="klein leise">
                            {GEWERK_TEXT[g.art]}, {k2.verfasser}, {datum(k2.zeitpunkt)}
                          </div>
                          <div>{k2.text}</div>
                        </div>
                      ))
                    )}
                  </div>
                )}

                {/* WANN WURDE DAS UMGEBUCHT, UND VON WEM.
                    Die Zeilen dazu werden seit Migration 006 bei jeder
                    Buchung geschrieben und wurden von nichts angezeigt.
                    Zugeklappt, weil sie bei einem ruhigen Auftrag genau eine
                    Zeile lang sind und bei einem unruhigen zwanzig. */}
                {d.gewerke.some((g: any) => g.terminverlauf?.length) && (
                  <details style={{ marginTop: 14 }}>
                    <summary className="klein">Terminverlauf</summary>
                    {/* SECHS SPALTEN ROLLEN AUF 375 PIXELN, UND ZWAR IN IHREM
                        EIGENEN KASTEN. Der Rückfall `.karte > .leib` greift
                        hier nicht: die Tabelle liegt eine Etage tiefer, in
                        einem zugeklappten `<details>`, und dessen Inhalt
                        zählt nicht in die Rollbreite der Karte. Aufgefallen
                        ist es erst an einem Auftrag MIT Terminverlauf - der
                        Block entsteht nur dann, und die Prüfdatenbank hatte
                        bis zur Übernahme keinen. **Zweiter Fall derselben
                        Sache in dieser Version** nach den beiden Tabellen der
                        Übernahme: ein `<details>` ist ein Bereich, den
                        niemand ansieht, solange er zu ist. */}
                    <div className="leib rollt">
                      <table className="liste">
                        <thead>
                          <tr>
                            <th>Wann</th>
                            <th>Gewerk</th>
                            <th>Was</th>
                            <th>Von</th>
                            <th>Nach</th>
                            <th>Wer</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* ALLE GEWERKE IN EINER ZEITLEISTE.
                              Getrennt je Gewerk stünde die Umbuchung des
                              Einblasens über der drei Wochen älteren des
                              Tiefbaus, und der Zusammenhang „verschoben, weil
                              der Tiefbau rutschte" wäre nicht mehr zu sehen. */}
                          {d.gewerke
                            .flatMap((g: any) => (g.terminverlauf ?? [])
                              .map((v: any) => ({ ...v, art: g.art })))
                            .sort((a: any, b: any) =>
                              String(b.zeitpunkt).localeCompare(String(a.zeitpunkt)))
                            .map((v: any, i: number) => (
                              <tr key={i}>
                                <td className="klein">{zeitpunkt(v.zeitpunkt)}</td>
                                <td className="klein">{GEWERK_TEXT[v.art]}</td>
                                <td className="klein">
                                  {TERMIN_AKTION[v.aktion] ?? v.aktion}
                                  {v.grund && (
                                    <div className="leise">{v.grund}</div>
                                  )}
                                </td>
                                <td className="klein">
                                  {v.datum_alt ? datum(v.datum_alt) : ''}{v.datum_alt && v.zeit_alt ? `, ${v.zeit_alt} Uhr` : ''}
                                  {v.firma_alt && (
                                    <div className="leise">{v.firma_alt}</div>
                                  )}
                                </td>
                                <td className="klein">
                                  {v.datum_neu ? datum(v.datum_neu) : ''}{v.datum_neu && v.zeit_neu ? `, ${v.zeit_neu} Uhr` : ''}
                                  {v.firma_neu && (
                                    <div className="leise">{v.firma_neu}</div>
                                  )}
                                </td>
                                <td className="klein">
                                  {v.wer ?? <span className="leise">System</span>}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            </div>
            )}

            {/* ---------------------------------------------- Wiedervorlagen */}
            {(darfErinnern || (d.erinnerungen ?? []).length > 0) && (
              <Wiedervorlagen
                zeilen={d.erinnerungen ?? []} darf={darfErinnern}
                darfGbgs={darf(ich, 'gbgs.eintragen')}
                auftragId={auftragId} laden={laden} />
            )}

            {/* ---------------------------------------------- Aufmaß */}
            {d.aufmass.length > 0 && (
              <div className="karte">
                <h3>
                  Aufmaß
                  {!ich.istSub && (
                    <span className="intern-hinweis" style={{ marginLeft: 'auto', textTransform: 'none', letterSpacing: 0 }}>
                      Menge MDS und Bemerkung sind intern
                    </span>
                  )}
                  <Kartenweg hin={zumAufmass} text="Aufmaß bearbeiten" />
                </h3>
                <div className="leib">
                  <table className="liste">
                    <thead>
                      <tr>
                        <th style={{ width: 92 }}>OZ</th>
                        <th>Leistung</th>
                        <th>Abschnitt</th>
                        <th className="zahl" style={{ width: 90 }}>Sub</th>
                        {!ich.istSub && (
                          <>
                            <th className="zahl" style={{ width: 90 }}>MDS</th>
                            <th className="zahl" style={{ width: 80 }}>Diff.</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {d.aufmass.map((p: any) => (
                        <tr key={p.id} style={{ cursor: 'default' }}>
                          <td className="klein">{p.oz}</td>
                          <td>
                            {p.bezeichnung}
                            {p.herkunft === 'system' && (
                              <span className="leise klein">, Innendienst</span>
                            )}
                          </td>
                          <td className="klein leise">
                            {abschnittName(p.bauabschnitt_id) ?? '-'}
                          </td>
                          <td className="zahl">{menge(p.menge_sub, p.einheit)}</td>
                          {!ich.istSub && (
                            <>
                              <td className="zahl">
                                <span className="intern">{menge(p.menge_mds, p.einheit)}</span>
                              </td>
                              <td
                                className="zahl"
                                style={{ color: p.differenz ? 'var(--ueberfaellig)' : undefined }}
                              >
                                {p.differenz ? menge(p.differenz) : ''}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {berichtigen && (
              <EintragBerichtigen
                e={berichtigen} auftragId={auftragId}
                zu={() => setBerichtigen(null)}
                fertig={async () => { setBerichtigen(null); await laden(); }} />
            )}

            {loeschen && (
              <EintragLoeschen
                e={loeschen} auftragId={auftragId}
                zu={() => setLoeschen(null)}
                fertig={async () => { setLoeschen(null); await laden(); }} />
            )}

            {/* ---------------------------------------------- Verlauf
              *
              * DIE KARTE HING AN KEINEM BLOCK, und der Server liefert
              * `bloecke.auftragsinfo` seit Migration 040. Wer das Recht nicht
              * hat, bekommt die Einträge gar nicht erst geschickt - hier stand
              * dann eine Karte mit der Überschrift „Auftragsinfo (0)" und dem
              * Satz „noch nichts vermerkt". Das ist eine falsche Auskunft: es
              * IST etwas vermerkt, er darf es nur nicht sehen.
              * Seit dem 02.09.2026 hängt ausserdem der Rückweg daran: der
              * Menüpunkt „Kommentar" im Werkzeugmenü erscheint genau dann,
              * wenn es diese Karte NICHT gibt. */}
            {d.bloecke?.auftragsinfo !== false && (
            <div className="karte">
              {/* „VERLAUF" WAR DER FALSCHE NAME.
                *
                * ORIGINALTEXT MARVIN (31.08.2026)
                *   „Und Verlauf, das ist ja unsere Auftragsinfo, also Verlauf
                *    ist da der falsche Begriff, weil das ist nicht der Verlauf
                *    der WFMT-Verlauf, sondern unserer."
                *
                * Die Telekom liefert einen eigenen Verlauf mit (die Bemerkung
                * aus dem Vorsystem). Zwei Dinge mit demselben Namen an einem
                * Auftrag sind eine Verwechslung, die genau einmal teuer wird -
                * wenn jemand hier sucht, was dort steht. */}
              <h3>
                Auftragsinfo ({d.ereignisse.length})
                <Kartenweg hin={zumEintrag} text="Eintrag schreiben" />
                {/* .498: VORLAGE WÄHLEN, KLICK, ENDE. Marvin: „dass ich das
                    textlich nicht immer wieder schreiben muss." */}
                {zumEintrag && <AuftragsinfoVorlage auftragId={auftragId} laden={laden} />}
              </h3>
              <div className="leib">
                {/* DIE NAMEN WERDEN EINMAL FÜR DIE GANZE LISTE GERECHNET.
                    Ob ein Vorname reicht, ist keine Eigenschaft der EINZELNEN
                    Zeile, sondern der Liste: er reicht, solange in ihr nicht
                    zwei verschiedene Menschen so heissen. Je Zeile gerechnet
                    stünde die Antwort vierzigmal da und wäre vierzigmal
                    dieselbe halbe. */}
                {/* DIE LISTE STEHT EINMAL IM HAUS (`teile/Auftragsinfoliste`).
                    Sie stand hier ausgeschrieben und im Popup des Wochenplans
                    ein zweites Mal - und die zweite hatte kein Gesicht, keinen
                    kurzen Namen und den rohen Quellwert daneben. */}
                <Auftragsinfoliste
                  zeilen={d.ereignisse} deckel={40}
                  darfEintrag={darfEintrag}
                  aufBerichtigen={berichtige}
                  aufLoeschen={setLoeschen} />
                {d.ereignisse.length === 0 && (
                  <p className="leise klein" style={{ margin: 0 }}>
                    Für diesen Auftrag ist noch nichts vermerkt.
                  </p>
                )}
              </div>
            </div>
            )}

            {/* ------------------------------------------ Umgebungsbilder
              *
              * ORIGINALTEXT MARVIN (08.09.2026)
              *   „Auftragsakte: neuer Block «Umgebungsbilder» unter der
              *    Auftragsinfo."
              *
              * SIE LAGEN IM DATEIREITER, zwischen sechzehn Ordnern. Wer vor
              * der Frage steht „wie sieht es dort aus", macht die AKTE auf und
              * nicht die Dateiliste - und dort standen sie an einer Stelle,
              * die man kennen muss.
              *
              * DIE KARTE STEHT NUR DA, WENN BILDER DA SIND. Eine Karte, die
              * fast immer leer ist, wird nach zwei Wochen nicht mehr gelesen -
              * und dann fehlt sie an dem Tag, an dem etwas darin steht. Wo man
              * sie erzeugt, sagt der Lageplan; ein Kasten „noch keine Bilder"
              * mit einem Weg dorthin wäre die dritte Stelle, die dasselbe
              * sagt. */}
            {(d.umgebungsbilder?.length ?? 0) > 0 && (
            <div className="karte">
              <h3>Umgebungsbilder ({d.umgebungsbilder.length})</h3>
              <div className="leib">
                {/* DASSELBE GITTER WIE IM DATEIREITER. Zwei Fassungen
                    derselben Kachel laufen beim ersten Nachtrag auseinander,
                    und dann sieht dieselbe Datei auf zwei Bildschirmen
                    verschieden aus. */}
                <div className="dateigitter">
                  {/* DIESELBE KACHEL WIE IM DATEIREITER, UND ZWAR WIRKLICH
                      DIESELBE. Hier stand eine zweite Fassung, und sie konnte
                      weniger: sie zeichnete IMMER ein `<img>`, also stand eine
                      PDF als kaputtes Bildzeichen da. Der Kommentar darüber
                      sagte, dass zwei Fassungen auseinanderlaufen - das war
                      eine Ankündigung und keine Begründung.

                      DER NAME GEHÖRT AN DAS, WAS ER BENENNT. Er sagt, wohin
                      geschaut wird („Lage 1 Blick auf die Grube") - ohne ihn
                      sind vier Bilder derselben Kreuzung vier Bilder ohne
                      Auskunft. Er steht in der Kachel selbst. */}
                  {d.umgebungsbilder.map((b: any) => (
                    <Dateikachel key={b.id} datei={b}
                                 oeffne={(x) => zeigeDatei(x, d.umgebungsbilder)} />
                  ))}
                </div>
              </div>
            </div>
            )}

            {/* ------------------------------------------------------- Post
              *
              * ORIGINALTEXT MARVIN (08.09.2026, Punkt 7 der offenen Liste)
              *   „Postfach: Design, Thumbnails der Anhänge, Anhänge in einem
              *    Fenster statt neuem Tab, Büroklammer statt Mailsymbol am
              *    Auftrag, verschobene Popups"
              *
              * SEIN SATZ HATTE KEINEN GEGENSTAND, UND DAS WAR DER FUND. „Am
              * Auftrag" gab es weder ein Mailsymbol noch eine Klammer noch
              * eine Zeile: `GET /auftraege/:id/postfach` steht im Server, es
              * gibt `api.postfachAmAuftrag` in `api.ts`, und **keine einzige
              * Zeile der Oberfläche hat beides je gerufen.** Der Kommentar am
              * Weg versprach sogar „wer das Recht nicht hat, bekommt die
              * Karte gar nicht erst" - eine Karte, die es nicht gab.
              *
              * **Ein Weg, den der Server kann und keine Oberfläche geht, ist
              * von einem nicht gebauten nicht zu unterscheiden** - und er
              * sieht im Programmcode vollständig aus, weil die teure Hälfte
              * dasteht. Siebter Fall nach dem mehrtägigen Tiefbau, dem
              * Anlegen eines Auftrags von Hand, dem Rechteraster am Zugang,
              * der Bündelung der Terminmeldung, `nachweisPfad` und dem
              * internen Aufmaßauszug.
              *
              * DAS RECHT IST `postfach.lesen` UND KEIN EIGENES `akte.*`.
              * Es ist dasselbe, das der Weg dahinter prüft; ein zweites
              * daneben wären zwei Stellen für dieselbe Frage, und die zweite
              * pflegt niemand. Im Schriftverkehr mit einer Behörde stehen
              * Dinge, die eine Partnerfirma nichts angehen - wer das Recht
              * nicht hat, bekommt die Karte nicht, und das ist genau, was der
              * Weg seit jeher zusagt. */}
            {darf(ich, 'postfach.lesen') && (
              <PostAmAuftrag auftragId={auftragId} />
            )}
          </div>

          {/* ------------------------------------------------ Spalte rechts */}
          <div>
            {/* ------------------------------------------- Baustelle
              *
              * ORIGINALTEXT MARVIN (23.08.2026, Bildschirmfoto)
              *   „dann hast du hier eine völlig falsche darstellung mit
              *    underschiedlichen schriften und größen."
              *
              * Vorher standen hier drei Schriftgrößen und ein unterstrichener
              * Telefonlink. Jetzt: eine Größe für alle Werte, eine für alle
              * Beschriftungen, Kennungen in Ziffernschrift, und alles, was
              * eine Erläuterung ist, als Nebenzeile darunter.
              */}
            {/* VIER KARTEN STATT EINER, UND JEDE HAENGT AN IHREM RECHT.
              *
              * ORIGINALTEXT MARVIN (26.08.2026)
              *   „Der sub sieht nur sein Gewerk. Er braucht keine Infos zur
              *    Gemeinde oder sonst was."
              *   „Soll über die Rollen doch eindeutig zugewiesen werden. Ich
              *    will es flexibel halten."
              *
              * Hier stand EINE Liste mit sechzehn Feldern: SM-Nummer, KLS,
              * Adresse, Gemeinde, Kunde, Auftragstyp, Status Telekom,
              * Priorität, Tiefbau im, NVt, NVt-Standort, Rohrverband,
              * Röhrchenfarbe, Röhrchenbreite, Belegnummer. Für den Innendienst
              * ist das die Akte. Für einen Monteur auf der Baustelle sind
              * zwei Drittel davon Lärm, und die Gemeinde geht ihn nichts an.
              *
              * Die Aufteilung folgt dem Bestand (MobileAppSeite.html,
              * zeigeSubAuftrag): Baustelle, Kunde, Technik - und alles, was
              * Verwaltung ist, in einem vierten Block. Was ankommt,
              * entscheidet der Server über `d.bloecke`; hier wird nur noch
              * gezeichnet, was da ist. */}

            {d.bloecke?.baustelle !== false && (
            <div className="karte">
              <h3>
                Baustelle
                <Kartenweg hin={zumVorbereiten}
                           fertig={vorbereitungFertig}
                           text={vorbereitungFertig
                                   ? 'Bereits vorbereitet' : 'Vorbereiten'} />
              </h3>
              <div className="leib">
                <dl className="felder">
                  <dt>Adresse</dt>
                  <dd>
                    {k.strasse} {k.hausnummer}{k.hausnummer_zusatz ?? ''}
                    <div className="neben">{k.plz} {k.ort_text}</div>
                  </dd>

                  {/* GANZ OBEN UND SAUBER GETRENNT.
                      Marvin, 25.08.2026: „ich sagte rechts unter der Baustelle
                      soll die SM und KLS ID sauber getrennt stehen." Es sind
                      die zwei Zahlen, die am Telefon genannt und in Portale
                      getippt werden. */}
                  {/* UND MIT EINEM KLICK IN DER ZWISCHENABLAGE.
                      ORIGINALTEXT MARVIN (11.09.2026): „SM/KLS onclick
                      zwischenablage ist noch nicht eingebunden."
                      Er hat recht: die Version .155 hat sie nur im LEB-Export
                      eingebaut, und genannt und getippt werden die beiden
                      Zahlen HIER - in der Akte, am Telefon, neben dem
                      Portal. **Ein Weg, den es nur an einer Stelle gibt, ist
                      an den 3 anderen keiner.** */}
                  <dt>KLS-ID</dt>
                  <dd className="kennung">
                    <Kopierbar wert={k.kls_id} was="KLS-ID" />
                  </dd>

                  <dt>SM-Nummer</dt>
                  <dd className="kennung">
                    <Kopierbar wert={k.sm_nummer} was="SM-Nummer" />
                  </dd>

                  {/* DAS BAFA-EINGANGSDATUM (23.09.2026).
                      Marvin: „außerdem brauchen wir in den aufträgen das
                      BAFA Eingang Datum. es kommt mit dem report zu jedem
                      auftrag ins system und gab es auch schon in der alten
                      liste. baue es in der auftragsakte sichtbar ein."
                      `bafa_eingang` stand seit Migration 001 in der Tabelle
                      und wurde nirgends gezeigt. */}
                  <dt>BAFA-Eingang</dt>
                  <dd>
                    {k.bafa_eingang_tag
                      ? datum(k.bafa_eingang_tag)
                      : <span className="leise">fehlt - kommt mit dem nächsten Report</span>}
                  </dd>

                  {/* BULK ODER IBT - DIE ANGABE DER TELEKOM ZUR BAUSTELLE.
                      =========================================================
                      ORIGINALTEXT MARVIN (07.09.2026)
                        „Bulk oder IBT in Block «Baustelle» einfügen, diese
                         info fehlt im moment komplett."

                      **SIE FEHLTE NICHT IN DEN DATEN, SONDERN AUF DEM
                      BILDSCHIRM.** `auftragstyp_tk` kommt aus der Spalte
                      „Auftrag Typ" des Abrufs, wird bei jedem Import gepflegt,
                      steht seit Migration 001 in der Tabelle und wird vom
                      Server ausgeliefert - gelesen hat sie **keine einzige
                      Zeile der Oberfläche**. Dieselbe Sache wie die ONKz am
                      31.08.2026: *eine Angabe, die man hat und nicht zeigt,
                      ist so gut wie keine.*

                      SIE STEHT IM BLOCK BAUSTELLE UND NICHT IN „VERWALTUNG",
                      obwohl der Server sie dort maskiert. Das ist kein
                      Widerspruch: die Maskierung entscheidet, WER sie sieht
                      (eine Partnerfirma nicht), dieser Block entscheidet, WO
                      sie steht. Fehlt der Block „Verwaltung", kommt das Feld
                      gar nicht erst mit, und dann steht hier nichts.

                      MEHRERE WERTE MIT KOMMA: es ist eine Liste. Ein Auftrag
                      trägt „Hausanschluss, Wohnungsanschluss", und wer nur den
                      ersten zeigte, verschwiege den zweiten. */}
                  {/* UND WO ER ZU AENDERN IST, STEHT DANEBEN.
                      ORIGINALTEXT MARVIN (09.09.2026): „ich kann den
                      Auftragstyp (Telekom) nirgendwo eintragen bzw ändern."
                      Er hat im Reiter „Baustelle" des Bearbeiten-Fensters
                      gesucht, weil der Wert in DIESER Karte steht - und dort
                      liegen nur Adressfelder. Das Feld gibt es seit .150, im
                      Reiter „Verwaltung"; ein Wert, den man liest und nicht
                      findet, ist so gut wie keiner. */}
                  {k.auftragstyp_tk !== undefined && (
                    <>
                      <dt>Auftrag Typ (Telekom)</dt>
                      <dd>
                        {(k.auftragstyp_tk ?? []).length
                          ? (k.auftragstyp_tk as string[]).join(', ')
                          : <span className="leise">fehlt</span>}
                        <div className="klein leise">
                          zu ändern unter „Bearbeiten", Reiter „Verwaltung"
                        </div>
                      </dd>
                    </>
                  )}
                </dl>

                {/* DER KNOPF, NICHT DER LINK.
                    Auf der Baustelle wird er mit dem Daumen getroffen, im
                    Regen und mit Handschuh. Früher stand in Spalte I ein
                    GESPEICHERTER Maps-Link; hier wird er gerechnet: ein
                    gespeicherter Link neben einer gespeicherten Adresse sind
                    zwei Wahrheiten, und nach der ersten Adresskorrektur führt
                    er zum alten Haus. */}
                <div className="reihe wegknoepfe">
                  {navUrl(k) && (
                    <a className="knopf haupt" href={navUrl(k)}
                       target="_blank" rel="noreferrer">
                      Navigation starten
                    </a>
                  )}
                </div>
              </div>
            </div>
            )}

            {d.bloecke?.kunde !== false && (
            <div className="karte">
              <h3>
                Kunde
                <Kartenweg hin={zumBereich('kunde')} text="Kundendaten ändern" />
              </h3>
              <div className="leib">
                <dl className="felder">
                  <dt>Name</dt>
                  <dd>
                    {k.kunde_name ?? <span className="leise">nicht erfasst</span>}
                  </dd>
                  {d.bloecke?.kunde_mail && k.kunde_email && (
                    <>
                      <dt>Mail</dt>
                      <dd><a href={'mailto:' + k.kunde_email}>{mailMitUmbruch(k.kunde_email)}</a></dd>
                    </>
                  )}
                </dl>
                {/* EINE NUMMER ZUM ANTIPPEN, KEIN UNTERSTRICHENER TEXT.
                    Marvin am 20.08.2026 zur Sub-App: die Mailadresse des
                    Kunden geht die Partnerfirma nichts an, die Nummer schon -
                    er steht vor dem Haus und niemand macht auf. */}
                <div className="reihe wegknoepfe">
                  {k.kunde_telefon && (
                    <a className="knopf" href={'tel:' + k.kunde_telefon}>
                      {k.kunde_telefon}
                    </a>
                  )}
                  {k.kunde_mobil && (
                    <a className="knopf" href={'tel:' + k.kunde_mobil}>
                      {k.kunde_mobil}
                    </a>
                  )}
                  {!k.kunde_telefon && !k.kunde_mobil && (
                    <span className="leise klein">Keine Rufnummer erfasst.</span>
                  )}
                </div>

                {/* ==========================================================
                    „WIR HABEN DEN KUNDEN NICHT ERREICHT."
                    ==========================================================
                    ORIGINALTEXT MARVIN (03.09.2026)
                      „wo starten wir denn die automatik, dass der kunde
                       angemailt wird wenn wir ihn nicht erreicht haben? Der
                       auftrag mus sja dann mit ‚wartet' gekennzeichnet werden.
                       und sobald ein termin gemacht wird oder wir manuell
                       diese automatik wieder aufheben"

                    ER STEHT HIER UND NICHT IN EINEM MENÜ: die Frage entsteht
                    beim Blick auf die Rufnummer, direkt nach dem Anruf, der
                    niemanden erreicht hat. */}
                {/* UND SOLANGE DIE AUTOMATIK LÄUFT, IST ER ROT (08.09.2026).

                    ORIGINALTEXT MARVIN
                      „«Kunde nicht erreicht»: Punkt wird rot, solange die
                       Automatik läuft, und lässt sich an derselben Stelle
                       wieder aufheben"

                    Hier stand die Warnfarbe. Sie ist in diesem Haus „zum
                    Nachsehen", und dieser Zustand ist mehr: der Auftrag steht
                    auf „warten", er fällt aus der Liste der laufenden, und
                    jede Woche geht eine Mail an einen Kunden hinaus. **Eine
                    Farbe, die weniger behauptet als der Zustand hergibt, wird
                    beim zwanzigsten Mal überlesen.**

                    AUFGEHOBEN WIRD ER AN DERSELBEN STELLE, und das war schon
                    so - es fehlte allein die Farbe und der Satz, WAS gerade
                    läuft. */}
                {darf(ich, 'auftrag.schreiben') && (
                  k.nachfassen_seit ? (
                    <div className="hinweis fehler" style={{ marginTop: 10 }}>
                      <b>
                        <span className="punkt r" style={{ marginRight: 6 }} />
                        Kunde nicht erreicht - wird nachgefasst
                        seit {datum(k.nachfassen_seit)}.
                      </b>
                      {k.nachfassen_grund && <div>{k.nachfassen_grund}</div>}
                      <div className="klein">
                        Der Auftrag steht auf „warten", und der Kunde bekommt
                        die Erinnerungen der Automatik. Beides endet mit der
                        ersten Terminvergabe oder hier.
                      </div>
                      <div className="reihe" style={{ marginTop: 8 }}>
                        <button className="knopf"
                                onClick={() => springe('nachfassen')}>
                          Nachfassen beenden
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="reihe" style={{ marginTop: 10 }}>
                      <button className="knopf"
                              onClick={() => springe('nachfassen')}>
                        Kunde nicht erreicht
                      </button>
                    </div>
                  )
                )}
              </div>
            </div>
            )}

            {/* ==============================================================
                DER TIEFBAUAUFWAND.
                ==============================================================
                ORIGINALTEXT MARVIN (03.09.2026)
                  „die info zum tiefbauaufwand sollten wir noch als block in
                   die auftragsakte bringen und hier bei dem
                   terminsteuerungsfenster mit anzeigen."

                ER STAND NUR IM VORBEREITEN-DIALOG - hinter zwei Klicks und
                einem Fenster, das die Karte lädt. Wer wissen will, ob ein Tag
                reicht, macht keinen Zeichendialog auf.

                UND ER HÄNGT AN EINEM EIGENEN RECHT (`akte.planung`). Die
                Schätzung ist unsere Kalkulation: Stunden und Tage, aus denen
                sich ablesen lässt, womit wir rechnen. Sie gehört keinem
                Auftragnehmer - deshalb kommt sie ohne das Recht gar nicht
                erst mit. */}
            {/* UND OHNE SCHÄTZUNG STEHT DA, DASS ES KEINE GIBT.
                ==============================================================
                Hier stand `d.bloecke?.planung && d.tiefbauaufwand` - die
                Karte fiel weg, sobald noch niemand gezeichnet hatte. Auf
                einem frischen Auftrag ist das der REGELFALL, und damit sah
                der Bildschirm genauso aus wie vor dieser Version: **eine
                fehlende Karte ist von einer nicht gebauten nicht zu
                unterscheiden.** Genau daran ist am 03.09.2026 die Meldung
                „ich sehe die neue funktion nicht" entstanden.

                Ein leerer Block, der nicht sagt, wo man ihn füllt, wäre
                allerdings genauso wenig wert - deshalb steht der Weg zur
                Planung darin. */}
            {/* GBGS (.357). Marvin, 14.10.2026: „im Auftrag selber unter GBGS einen
                Link ... um einen Wartegrund und so weiter zu setzen dort. Und dass es
                dann von da aus ausgelöst wird." */}
            {k.kls_id && !ich.istSub && darf(ich, 'gbgs.lesen') && (
              <GbgsKarte ich={ich} kls={String(k.kls_id)} auftragId={auftragId}
                         bulkAuftrag={(k.auftragstyp_tk ?? []).some((t: string) => String(t).toUpperCase() === 'BULK')} />
            )}

            {d.bloecke?.planung && !d.tiefbauaufwand && (
            <div className="karte">
              <h3>
                Tiefbauaufwand
                <Kartenweg hin={zurPlanung} text="Planung öffnen" />
              </h3>
              <div className="leib">
                <p className="klein leise" style={{ margin: 0 }}>
                  Noch nicht geschätzt. Über „Planung öffnen" den Leitungsweg
                  einzeichnen - daraus entstehen Meter, Stunden und Tage, und
                  die stehen dann hier und beim Buchen eines Tiefbautermins.
                </p>
              </div>
            </div>
            )}

            {d.bloecke?.planung && d.tiefbauaufwand && (
            <div className="karte">
              <h3>
                Tiefbauaufwand
                <Kartenweg hin={d.planungskarte ? () => setPlanungskarteOffen(true) : null} text="Karte ansehen" />
                <Kartenweg hin={zurPlanung} text="Planung öffnen" />
              </h3>
              <div className="leib">
                <div className="kachelreihe">
                  <div className="kachel">
                    <div className="wert">
                      {menge(d.tiefbauaufwand.meter)}<span className="was"> m</span>
                    </div>
                    <div className="was">Trasse</div>
                  </div>
                  <div className="kachel">
                    <div className="wert">
                      {menge(d.tiefbauaufwand.stunden)}
                      <span className="was"> h</span>
                    </div>
                    <div className="was">geschätzt</div>
                  </div>
                  <div className="kachel">
                    <div className="wert">{menge(d.tiefbauaufwand.tage)}</div>
                    <div className="was">
                      {Number(d.tiefbauaufwand.tage) === 1 ? 'Tag' : 'Tage'}
                    </div>
                  </div>
                </div>
                <dl className="felder">
                  {Number(d.tiefbauaufwand.leerrohr_meter) > 0 && (
                    <>
                      <dt>davon Leerrohr</dt>
                      <dd>{menge(d.tiefbauaufwand.leerrohr_meter)} m</dd>
                    </>
                  )}
                  <dt>Montagegruben</dt>
                  <dd>{d.tiefbauaufwand.montagegruben ?? 0}</dd>
                  <dt>Hauseinführungen</dt>
                  <dd>{d.tiefbauaufwand.hauseinfuehrungen ?? 1}</dd>
                  <dt>Oberfläche</dt>
                  {/* EINE LEERE ZEICHENKETTE IST KEINE ANGABE. Hier stand nur
                      `?? …`, und das greift bei `null` - der Server liefert
                      seit .137 immer einen Satz, und der ist leer, solange
                      nichts gezeichnet ist. Ohne diese Zeile stünde dort eine
                      leere Zelle statt „nicht bestimmt". */}
                  <dd>{d.tiefbauaufwand.oberflaeche
                        ? d.tiefbauaufwand.oberflaeche
                        : <span className="leise">nicht bestimmt</span>}</dd>
                </dl>
              </div>
            </div>
            )}

            {/* DER GEPLANTE LEITUNGSWEG FÜR DIE PARTNER (.455). Marvin,
                22.10.2026: „sowohl für uns als auch für die Partner".
                Ohne Stunden und Tage: die gehören keinem Auftragnehmer. */}
            {!d.bloecke?.planung && d.planungskarte && (
            <div className="karte">
              <h3>
                Geplanter Leitungsweg
                <Kartenweg hin={() => setPlanungskarteOffen(true)} text="Karte ansehen" />
              </h3>
              <div className="leib">
                <p className="klein leise" style={{ margin: 0 }}>
                  Wir haben den Graben und die Gruben auf dem Luftbild
                  eingezeichnet, mit der Oberfläche je Abschnitt.
                </p>
              </div>
            </div>
            )}
            {planungskarteOffen && (
              <Planungskarte auftragId={auftragId} zu={() => setPlanungskarteOffen(false)} />
            )}

            {d.bloecke?.technik !== false && (
            <div className="karte">
              <h3>
                Technik
                <Kartenweg hin={zumBereich('netz')} text="Technik ändern" />
              </h3>
              <div className="leib">
                <dl className="felder">
                  <dt>NVt</dt>
                  <dd className="kennung">
                    {k.nvt ?? <span className="leise">fehlt</span>}
                    {/* EIN FRAGEZEICHEN IST KEINE ANGABE. Hier stand
                        „ASB ?, ONKz ?", auch wenn beides fehlte: eine Zeile,
                        die immer dasteht und nie etwas sagt, wird nicht
                        gelesen - auch an dem Tag nicht, an dem etwas
                        drinsteht. Was fehlt, faellt jetzt weg. */}
                    {k.asb && <div className="neben">ASB {k.asb}</div>}
                  </dd>

                  {/* DIE VORWAHL ALS EIGENE ZEILE.
                    *
                    * ORIGINALTEXT MARVIN (31.08.2026)
                    *   „außerdem fehlt bei der technik noch die ONKz und die
                    *    Einheiten zum Haus"
                    *
                    * Sie STAND da - klein, als Beisatz unter dem NVt, neben
                    * der ASB. Das ist die Ortsnetzkennzahl und eine eigene
                    * Angabe: sie entscheidet, in welches Netz der Anschluss
                    * gehört, und steht auf jedem Behördenpapier. Ein Beisatz
                    * in kleiner Schrift wird nicht gelesen. */}
                  <dt>ONKz</dt>
                  <dd className="kennung">
                    {k.onkz ?? <span className="leise">fehlt</span>}
                    {/* OHNE „BAUAKTE SAGTE" (.405). Marvin: „die ONKZ muss nicht
                        mit dem vermerk aus der bauakte hinterlegt sein? es ist
                        kein double check notwendig." Die Zeile stand hier
                        immer, auch bei gleichem Wert (die Sicht kannte das
                        Feld nicht, Migration 526). */}
                  </dd>

                  {/* DIE EINHEITEN ZUM HAUS.
                    *
                    * Ein Mehrfamilienhaus bekommt im Abruf je WOHNUNG eine
                    * Zeile - die Hohenzollernstr. 123 trägt eine KLS-ID und
                    * vier Bauauftrag-IDen. Die Zahl dieser Kennungen IST die
                    * Zahl der Wohneinheiten (Migration 067).
                    *
                    * STEHT NICHTS DA, WIRD NICHTS BEHAUPTET. Eine „1" wäre
                    * hier eine Vermutung, die aussieht wie eine Feststellung -
                    * die Migration setzt sie deshalb nur, wo mehr als eine
                    * Kennung dasteht. */}
                  <dt>Einheiten</dt>
                  <dd>
                    {/* DREI ZAHLEN, WIE DER AUFTRAGGEBER SIE ZÄHLT.
                      *
                      * ORIGINALTEXT MARVIN (31.08.2026)
                      *   „es sind Wohneinheiten(WE), Geschäftseinheiten (GE),
                      *    Sonderleitungen (SL)"
                      *
                      * Keine Summe: eine Geschäftseinheit hat andere
                      * Zugangszeiten als eine Wohnung, und eine Sonderleitung
                      * ist überhaupt kein Anschluss im gewohnten Sinn. Wer
                      * alle drei zusammenwirft, kann aus der Zahl nicht mehr
                      * ablesen, was auf der Baustelle zu erwarten ist.
                      *
                      * WAS NICHT ERFASST IST, STEHT NICHT ALS NULL DA. Eine
                      * 0 ist eine Feststellung („hier ist kein Geschäft"),
                      * ein Strich ist eine Auskunft („wir wissen es nicht"). */}
                    {(k.einheiten_we != null || k.einheiten_ge != null
                      || k.einheiten_sl != null)
                      ? <>
                          {[['WE', k.einheiten_we], ['GE', k.einheiten_ge],
                            ['SL', k.einheiten_sl]]
                            .map(([n, v]: any) => (
                              <span key={n} style={{ marginRight: 12 }}>
                                {n} <b>{v ?? '–'}</b>
                              </span>
                            ))}
                          {(k.einheiten_we ?? 0) > 1 && (
                            <div className="neben">
                              Mehrfamilienhaus: je Wohnung ein Bauauftrag.
                            </div>
                          )}
                          {/* WOHER, SEIT WANN, DURCH WEN (.529). Marvin,
                              28.10.2026: „hier muss seitlich stehen, dass die
                              Einheiten angepasst wurden, am besten noch mit
                              datum und durch wen". Weicht die Hand vom GBGS
                              ab, steht der GBGS-Stand daneben. */}
                          <div className="neben">
                            {k.einheiten_quelle === 'hand' ? (
                              <>
                                Von Hand geändert
                                {k.einheiten_am ? ` am ${datum(k.einheiten_am)}` : ''}
                                {k.einheiten_von_name ? ` von ${k.einheiten_von_name}` : ''}
                                {k.einheiten_gbgs
                                  && (k.einheiten_gbgs.we !== k.einheiten_we || k.einheiten_gbgs.ge !== k.einheiten_ge
                                      || (k.einheiten_gbgs.sl != null && k.einheiten_gbgs.sl !== k.einheiten_sl))
                                  ? `; das GBGS sagt WE ${k.einheiten_gbgs.we ?? '–'}, GE ${k.einheiten_gbgs.ge ?? '–'}, SL ${k.einheiten_gbgs.sl ?? '–'} (Stand ${datum(k.einheiten_gbgs.am)}).`
                                  : '.'}
                              </>
                            ) : k.einheiten_quelle ? (
                              <>Aus dem GBGS{k.einheiten_quelle === 'gbgs_bulk' ? ' (Bulk)' : ''}{k.einheiten_am ? `, Stand ${datum(k.einheiten_am)}` : ''}.</>
                            ) : null}
                          </div>
                        </>
                      : <span className="leise">
                          nicht erfasst
                          {Array.isArray(k.kennungen)
                           && k.kennungen.filter((x: any) => x.art === 'bauauftrag').length > 1
                            ? ` (${k.kennungen.filter((x: any) => x.art === 'bauauftrag').length}`
                              + ' Bauaufträge stehen daran)'
                            : ''}
                        </span>}
                    {/* UND WO MAN SIE EINTRÄGT.
                        ORIGINALTEXT MARVIN (01.09.2026)
                          „ich sehe die Einheiten nicht bei dem auftrag an der
                           seite"
                        Er hat recht, und der Grund ist nicht die Anzeige: WE,
                        GE und SL stehen bei den meisten Aufträgen leer. Die
                        Migration schätzt die Wohneinheiten nur, wo MEHRERE
                        Bauaufträge an einer Adresse hängen - bei GE und SL
                        gibt es nichts zu schätzen, und **eine geratene Null
                        sieht aus wie eine gemessene**. Eintragen lassen sie
                        sich seit jeher unter „Technik ändern"; nur stand das
                        nirgends. **Ein Feld, das leer ist und nicht sagt, wo
                        man es füllt, ist von einem fehlenden Feld nicht zu
                        unterscheiden.** */}
                    {(k.einheiten_we == null && k.einheiten_ge == null
                      && k.einheiten_sl == null) && zumBereich('netz') && (
                      <div className="neben">
                        Einzutragen über „Technik ändern".
                      </div>
                    )}
                  </dd>

                  {/* DREI ANGABEN, DREI ZEILEN.
                      Marvin, 25.08.2026: „Rohrverband und rohrfarbe ist
                      getrennt gewesen, und es fehlt die Rohrbreite auch noch." */}
                  {/* WO DIE FARBE VOR ORT EINGETRAGEN WIRD, STEHT DANEBEN.
                   *
                   * ORIGINALTEXT MARVIN (08.09.2026)
                   *   „Außerdem sehe ich nicht, wo ich die Farbe und die
                   *    Meterangabe des Kabels ändern kann. das sind
                   *    essentielle dinge, wo mit überall gearbeitet werden."
                   *
                   * ES GEHT SEIT JEHER - nur stand das an dieser Stelle
                   * nirgends, und die Zeile daneben zeigt den Wert aus der
                   * BAUAKTE: wer sie liest, sieht eine Angabe, die er nicht
                   * ändern kann, und sucht den Weg dorthin.
                   *
                   * SEIT DEM 08.09.2026 FÜHRT DER WEG IN DIE KARTE „TIEFBAU".
                   * Marvin: „hier geht es um die rückmeldungen die der tiefbau
                   * draußen macht ... diese infos sollen dann ja für alle
                   * anderen sichtbar sein." Was vor Ort abweicht, wird
                   * ZURÜCKGEMELDET und nicht in einem Formular über den ganzen
                   * Auftrag geändert: die Rückmeldung trägt ihren Stempel und
                   * steht in der Auftragsinfo, die der Partner sieht.
                   *
                   * **Ein Feld, das man ändern darf und dessen Weg nirgends
                   * steht, ist von einem unveränderlichen nicht zu
                   * unterscheiden** - dieselbe Lehre wie bei den Einheiten
                   * eine Zeile weiter oben (01.09.2026).
                   *
                   * Der Hinweis steht NUR, wenn dieser Leser den Weg auch
                   * gehen darf (`zumBereich` prüft das Recht) - ein Hinweis
                   * auf einen Weg, den es für ihn nicht gibt, ist schlimmer
                   * als kein Hinweis. */}
                  <dt>Rohrverband</dt>
                  <dd>
                    {k.rohrverband ?? <span className="leise">fehlt</span>}
                    <Istwert wert={k.snr_rohrverband_ist} am={k.snr_ist_gemeldet_am}
                             name={k.snr_ist_name} />
                    <Bauaktenwert werte={d.bauaktenwerte} feld="rohrverband" />
                  </dd>

                  <dt>Röhrchenfarbe</dt>
                  <dd>
                    {k.roehrchen ?? <span className="leise">fehlt</span>}
                    <Istwert wert={k.snr_roehrchen_ist} am={k.snr_ist_gemeldet_am}
                             name={k.snr_ist_name} />
                    <Bauaktenwert werte={d.bauaktenwerte} feld="roehrchen" />
                  </dd>

                  {/* UND DIE DRITTE ANGABE HAT IHREN ISTWERT SEIT DEM
                      08.09.2026. Marvin nennt vier Werte, die vor Ort
                      abweichen - „länge des SNR, rohrfarbe, Verband und
                      rohrstärke". Zwei davon hatten seit .120 eine Istspalte,
                      zwei nicht; **eine halbe Rückmeldung ist die Sorte
                      Lücke, die niemandem auffällt: die zwei anderen Zeilen
                      sehen ja richtig aus.** */}
                  <dt>Röhrchenbreite</dt>
                  <dd>
                    {k.roehrchenbreite ?? <span className="leise">fehlt</span>}
                    <Istwert wert={k.snr_roehrchenbreite_ist} am={k.snr_ist_gemeldet_am}
                             name={k.snr_ist_name} />
                    <Bauaktenwert werte={d.bauaktenwerte} feld="roehrchenbreite" />
                  </dd>

                  {/* WAS DER TIEFBAU WIRKLICH VERLEGT HAT.
                   *
                   * ORIGINALTEXT MARVIN (31.08.2026)
                   *   „außerdem fehlen die textlichen infos wieviel meter
                   *    pipe der Tiefbau verlegt hat und evtl. die pipe
                   *    geändert hat. das ist was, was man unter ‚Gewerke' mit
                   *    reinbringen kann bzw auch im bereich ‚technik'
                   *    duplizieren."
                   *
                   * DIE FELDER GAB ES, UND SIE STANDEN AUF KEINEM
                   * LESEBILDSCHIRM. `snr_laenge_m` und `snr_pipe_vorhanden`
                   * waren nur im Änderungsformular zu sehen - also genau dort,
                   * wo man sie EINTRÄGT, und nirgends dort, wo man sie
                   * BRAUCHT. Der Monteur kommt zwei Wochen nach dem
                   * Tiefbauer und muss wissen, wie weit das Rohr reicht.
                   *
                   * Der „vor Ort"-Zusatz an Rohrverband und Röhrchenfarbe
                   * oben ist die andere Hälfte derselben Auskunft: was
                   * geplant war, steht in der Zeile, was wirklich liegt,
                   * darunter. */}
                  {/* „VERLEGT" WAR DIE FALSCHE AUFSCHRIFT.
                      ORIGINALTEXT MARVIN (01.09.2026)
                        „bei technik steht verlegt 15 meter, was sich auf die
                         länge der bauakte bezieht und nicht auf die
                         tatsächliche länge"
                      `snr_laenge_m` kommt aus dem Deckblatt der BAUAKTE, ist
                      also geplant und nicht gebaut. „Verlegt" liest sich wie
                      eine Feststellung über die Baustelle - und daneben stand
                      im Tiefbau eine andere Zahl. **Eine Aufschrift, die mehr
                      behauptet als die Zahl hergibt, ist schlimmer als eine
                      fehlende Zahl:** man rechnet damit. */}
                  {/* UND DIE BEIDEN ZAHLEN STEHEN NEBENEINANDER, MIT IHRER
                      HERKUNFT.
                      ORIGINALTEXT MARVIN (02.09.2026)
                        „hier ist wieder der unterschied der Pipelänge auf der
                         seite mehrmals drin"
                      Der Widerspruch entstand nicht dadurch, dass es zwei
                      Zahlen gibt - die gibt es wirklich, und sie können
                      auseinandergehen -, sondern dadurch, dass sie an drei
                      Stellen ohne Herkunft standen. Wer die geplante Zahl
                      zeigt, zeigt die gemessene daneben; dann ist der
                      Unterschied die Auskunft und nicht der Fehler. */}
                  {/* UND HIER STEHT DIE PIPE UND NICHT DER GRABEN.
                      Die Zeile heisst „Länge" und meint die SNR-Länge: sie
                      steht neben der Planangabe der Bauakte und neben dem
                      Istwert, den der Tiefbau zurückgemeldet hat. Aufgemessen
                      wird sie über 10030340, und genau diese Position ist
                      der alten Summe ganz durch die Lappen gegangen - sie
                      trägt mit Absicht kein Gewerk, weil eingeblasen wird und
                      nicht gegraben. */}
                  <dt>Länge</dt>
                  <dd>
                    {d.tiefbaustand?.pipe_aufgemessen != null ? (
                      <>
                        <b>{menge(d.tiefbaustand.pipe_aufgemessen)} m</b>
                        {' '}<span className="leise">Pipe aufgemessen</span>
                      </>
                    ) : (
                      <span className="leise">noch nicht aufgemessen</span>
                    )}
                    {/* DIE DRITTE ZAHL, UND SIE GEHÖRT ZUR ERSTEN.
                        `snr_laenge_ist_m` ist die vom Tiefbau BERICHTIGTE
                        Planangabe und nicht die aufgemessene Menge.

                        HIER STAND EIN ABSATZ AUS FÜNF SÄTZEN, und vier davon
                        waren Erklärung: wo man die geplante Länge einträgt,
                        woher die aufgemessene kommt, über welche Karte eine
                        Abweichung hereinkommt, und noch einmal die drei
                        Istwerte, die drei Zeilen weiter oben schon stehen.
                        **Was zweimal dasteht, wird beim zweiten Mal nicht
                        gelesen - und der Satz daneben, der wirklich etwas
                        sagt, mit ihm.** Übrig bleibt die Zahl mit ihrer
                        Herkunft. */}
                    {k.snr_laenge_ist_m != null && k.snr_laenge_ist_m !== '' ? (
                      <Istwert
                        wert={`${menge(k.snr_laenge_ist_m)} m`
                          + (k.snr_laenge_m != null && k.snr_laenge_m !== ''
                             ? ` statt ${menge(k.snr_laenge_m)} m laut Bauakte`
                             : '')}
                        am={k.snr_ist_gemeldet_am} name={k.snr_ist_name} />
                    ) : k.snr_laenge_m != null && k.snr_laenge_m !== '' ? (
                      <div className="neben">
                        Laut Bauakte {menge(k.snr_laenge_m)} m - geplant, nicht
                        gemessen.
                      </div>
                    ) : null}
                  </dd>

                  {/* DIE ADRESSE DES NETZVERTEILERS, mit dem Weg dorthin.
                      Marvin, 25.08.2026: „wo ist die adresse zum NVT? ... Ist
                      da der link dabei zum hinnavigieren?" Der Tiefbauer faehrt
                      zum Haus, der Monteur zum NVt - das sind zwei
                      verschiedene Orte, und der zweite stand nirgends. */}
                  <dt>NVt-Standort</dt>
                  <dd>
                    {k.nvt_adresse ?? (
                      <span className="leise">
                        nicht erfasst
                        <div className="neben">
                          Er kommt aus dem Deckblatt der Bauakte.
                        </div>
                      </span>
                    )}
                    <Bauaktenwert werte={d.bauaktenwerte} feld="nvt_adresse" />
                  </dd>
                </dl>

                {/* WARUM DIESE VIER FELDER LEER SIND.
                    ORIGINALTEXT MARVIN (26.08.2026)
                      „es gibt nur noch einzelfälle, wo die bauakte nicht das
                       neue format hat. wie gehen wir aber auch mit diesen um?"
                    So: der Auftrag sagt es an der Stelle, an der die Lücke
                    steht. Ohne diesen Satz sieht ein reiner Scan genauso aus
                    wie eine Bauakte, in der nichts drinstand - und niemand
                    weiss, ob er abtippen muss oder ob es nichts abzutippen
                    gibt. Steht der Merker, hat ein Leseversuch WIRKLICH
                    stattgefunden und nichts ergeben. */}
                {k.bauakte_stumm_am && (
                  <div className="hinweis warnung" style={{ marginTop: 10 }}>
                    <b>Die Bauakte gibt nichts her</b>
                    {k.bauakte_stumm_grund ?? 'Kein Feld erkannt.'}
                    {' '}Diese 4 Felder müssen von Hand aus dem Deckblatt
                    abgetippt werden; „Bearbeiten" oben im Kopf. Kommt später
                    eine lesbare Fassung herein, verschwindet dieser Hinweis
                    von selbst.
                  </div>
                )}

                <div className="reihe wegknoepfe">
                  {k.nvt_adresse && (
                    <a className="knopf"
                       href={'https://www.google.com/maps/dir/?api=1&destination='
                             + encodeURIComponent(k.nvt_adresse)}
                       target="_blank" rel="noreferrer">
                      Zum NVt navigieren
                    </a>
                  )}
                </div>
              </div>
            </div>
            )}

            {d.bloecke?.verwaltung !== false && (
            <div className="karte">
              <h3>
                Verwaltung
                <Kartenweg hin={zumBereich('auftrag')} text="Verwaltung ändern" />
              </h3>
              <div className="leib">
                <dl className="felder">
                  <dt>Gemeinde</dt>
                  <dd>
                    {k.gemeinde ?? <span className="leise">keine</span>}
                    {k.gemeinde && !k.gemeinde_gepflegt && (
                      <div className="neben warnung">
                        nicht gepflegt: keine Empfänger, Vorlauf {k.vorlauf_tage} Tage
                      </div>
                    )}
                  </dd>

                  <dt>Auftragstyp</dt>
                  <dd>
                    {k.auftragstyp_mds ?? <span className="leise">nicht gesetzt</span>}
                  </dd>

                  <dt>Status Telekom</dt>
                  <dd>
                    {(k.status_tk ?? []).join(', ')
                      || <span className="leise">keiner</span>}
                  </dd>

                  <dt>Priorität</dt>
                  <dd>{k.prio ?? <span className="leise">nicht gesetzt</span>}</dd>

                  <dt>Tiefbau im</dt>
                  <dd>
                    {BEREICH_TEXT[k.tb_bereich]
                      ?? <span className="leise">nicht entschieden</span>}
                    {k.kein_tiefbau && <div className="neben">ohne Tiefbau</div>}
                  </dd>

                  {/* ZWEI NUMMERN, DIE NICHT DASSELBE SIND.
                      ORIGINALTEXT MARVIN (02.09.2026): „das was du hier
                      verknüpft hast ist nicht die belegnummer, die
                      belegnummer beginnt mit 4200...."
                      Hier stand die „Auftragsnummer AG" aus dem BAFA-Bericht
                      unter der Aufschrift „Belegnummer". Die Belegnummer geht
                      in die LEB-Datei und trägt die Gutschrift; sie kommt vom
                      Sammeldruck der Abrufe. Eine Aufschrift, die mehr
                      behauptet als der Wert hergibt, ist schlimmer als eine
                      fehlende Zahl: man rechnet damit. */}
                  <dt>Belegnummer</dt>
                  <dd className="kennung">
                    {k.belegnummer ?? (
                      <span className="leise">
                        fehlt - sie kommt vom Sammeldruck der Abrufe
                      </span>
                    )}
                    {k.belegnummer && k.belegposition && (
                      <div className="neben">Position {k.belegposition}</div>
                    )}
                  </dd>

                  <dt>Auftragsnummer AG</dt>
                  <dd className="kennung">
                    {k.auftragsnummer_ag ?? <span className="leise">fehlt</span>}
                    <div className="neben">
                      aus dem BAFA-Bericht, nicht die Belegnummer
                    </div>
                  </dd>

                  {/* UNTER WELCHEM RAHMENVERTRAG DIESER AUFTRAG LÄUFT.
                      ORIGINALTEXT MARVIN (03.09.2026): „damit wir eine
                      Trennung haben, welcher Auftrag dann zukünftig über
                      welchen Rahmenvertrag reinkommt und dann entsprechend
                      auch die richtige Bepreisung haben."

                      FEHLT ER, IST DAS EINE AUSKUNFT UND KEINE LÜCKE: er
                      kommt mit dem Sammeldruck der Abrufe, und bis dahin
                      rechnet die Auswertung für diesen Auftrag mit null. Wo
                      man ihn von Hand setzt, steht daneben - ein Feld, das
                      leer ist und nicht sagt, wo man es füllt, ist von einem
                      fehlenden Feld nicht zu unterscheiden. */}
                  <dt>Rahmenvertrag</dt>
                  <dd className="kennung">
                    {k.rahmenvertrag ?? (
                      <span className="leise">
                        fehlt - er kommt vom Sammeldruck der Abrufe
                      </span>
                    )}
                    <div className="neben">
                      {k.rahmenvertrag
                        ? (k.rahmenvertrag_bezeichnung
                           ?? 'Preise in den Stammdaten unter „Rahmenverträge"')
                        : 'Ohne ihn fehlt dieser Auftrag in jeder '
                          + 'Preisauswertung.'}
                    </div>
                  </dd>
                </dl>
              </div>
            </div>
            )}

            {/* Was an diesem Auftrag nicht stimmt. Marvin, 23.08.2026: „wir
              * bauchen später valide daten." Steht in der Akte und nicht nur
              * im Systemzustand: hier wird es geradegezogen. */}
            {(d.befunde ?? []).length > 0 && (
              <div className="karte">
                <h3>Zu prüfen</h3>
                <div className="leib">
                  {d.befunde.map((b: any, i: number) => (
                    <div key={i} className="klein" style={{ marginBottom: 7 }}>
                      <span className={'gewerk ' + (b.schwere === 'hart' ? '' : 'fremd')}>
                        {b.schwere === 'hart' ? 'hält auf' : 'weich'}
                      </span>{' '}
                      {b.text}
                      {b.wert && <span className="kennung leise"> ({b.wert})</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* DIE ANORDNUNG GEHÖRT AUF DIE BAUSTELLE.
                Hier stand `!ich.istSub`, und der Tiefbauer kam damit nicht an
                das Papier, das ihm das Aufgraben erlaubt und das er bei einer
                Kontrolle vorzeigen muss. Der Server gibt ihm jetzt die
                ERLAUBNISPAPIERE (VAO, Ausnahmegenehmigung, Pollerfreigabe) und
                nur die - keine Gebühr, kein eigener Antrag, kein
                Versandstempel. Der Schalter `sub.sieht_anordnung` nimmt es
                zurück. Ist er aus, kommt eine leere Liste, und die Karte
                erscheint gar nicht. */}
            {(!ich.istSub || d.dokumente.length > 0) && (
              <div className="karte">
                <h3>
                  {ich.istSub ? 'Genehmigungen für diese Baustelle'
                              : 'Behördenpapiere'}
                  <Kartenweg hin={zurBehoerde} text="Behördenmappe" />
                </h3>
                <div className="leib">
                  {/* KEIN VERWEIS AUF EINEN REITER, DEN ER NICHT HAT.
                      Hier stand „Die PDF liegt im Reiter ,Dateien'". Seit die
                      Reiter an Rechten hängen, hat der Sub genau drei:
                      Übersicht, Aufmaß, Dateien. Das Papier öffnet sich
                      jetzt in der Zeile selbst - er zeigt es bei einer
                      Kontrolle vor, mit einem Daumen, im Stehen. */}
                  {ich.istSub && (
                    <p className="klein leise" style={{ marginTop: 0 }}>
                      Was die Behörde für diese Baustelle erlaubt hat.
                    </p>
                  )}
                  {d.dokumente.length === 0 && (
                    <p className="leise klein" style={{ margin: 0 }}>
                      Für diesen Auftrag liegt noch kein Papier vor.
                    </p>
                  )}
                  {/* HERKUNFT SCHLAEGT INHALT, und deshalb steht sie ZUERST.
                      Vorher stand der Name des Papiers vorn und die Herkunft
                      klein dahinter, in derselben blassen Marke wie der Stand.
                      Genau diese Verwechslung hat früher 158 falsche Haken
                      erzeugt: unser eigener Antrag heisst fast genauso wie der
                      Bescheid und enthaelt dieselben Woerter, ist aber KEIN
                      Nachweis und traegt weder Frist noch Gebuehr.

                      ORIGINALTEXT MARVIN (25.08.2026)
                        „«Wir» ersetzt du überall durch «MDS» und nimm unser
                         blau damit es sich abhebt."

                      Also: die Herkunft als erste Spalte, in fester Breite,
                      damit die Marken untereinander stehen; MDS im MDS-Blau,
                      die Behoerde in Gruen. Zwei Farben fuer zwei Bedeutungen,
                      nicht zwei Graustufen. */}
                  {d.dokumente.map((dk: any) => (
                    <div key={dk.id} className="papierzeile"
                         style={{ cursor: zurBehoerde ? 'pointer' : 'default' }}
                         title={zurBehoerde
                           ? 'In der Behördenmappe öffnen' : undefined}
                         onClick={zurBehoerde ?? undefined}>
                      <span
                        className={'herkunft ' + (dk.quelle === 'behoerde'
                          ? 'behoerde' : 'mds')}
                        title={dk.quelle === 'behoerde'
                          ? 'Von der Behörde: zählt als Nachweis und darf eine '
                            + 'Frist und eine Gebühr tragen.'
                          : 'Unser eigenes Papier: kein Nachweis, keine Frist, '
                            + 'keine Gebühr.'}
                      >
                        {dk.quelle === 'behoerde' ? 'Behörde' : 'MDS'}
                      </span>
                      <div>
                        <b>{DOK_TEXT[dk.art] ?? dk.art}</b>
                        <div className="klein leise">
                          {ZUSTAND_DOK[dk.zustand] ?? dk.zustand}
                          {dk.versendet_am && ` am ${datum(dk.versendet_am)}`}
                          {dk.aktenzeichen && `, ${dk.aktenzeichen}`}
                        </div>
                        {(dk.gueltig_von || dk.gueltig_bis) && (
                          <div className="klein">
                            gültig {datum(dk.gueltig_von)} bis {datum(dk.gueltig_bis)}
                            {!dk.frist_geprueft && (
                              <span style={{ color: 'var(--ueberfaellig)' }}>
                                , Herkunft ungeprüft
                              </span>
                            )}
                          </div>
                        )}
                        {/* GEBÜHRENBESCHEID ERWARTET (.306): blau. */}
                        {dk.gebuehr_erwartet && !ich.istSub && (
                          <div className="klein" style={{ marginTop: 4 }}>
                            <span className="merker erwartet">Gebührenbescheid erwartet</span>
                          </div>
                        )}
                        {dk.gebuehr_betrag != null && (
                          <div className="klein">
                            Gebühr {menge(dk.gebuehr_betrag)} EUR
                          </div>
                        )}
                        {ich.istSub && (
                          dk.datei ? (
                            <div className="reihe wegknoepfe">
                              <a className="knopf" href={api.dateiAdresse(dk.datei, dk.dateiname)}
                                 target="_blank" rel="noreferrer"
                                 onClick={(e) => e.stopPropagation()}>
                                {DOK_TEXT[dk.art] ?? dk.art} öffnen
                              </a>
                            </div>
                          ) : (
                            <div className="klein leise">
                              Die Datei dazu liegt noch nicht im System.
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Die Datei, die jemand gerade ansieht - in einem FENSTER und nicht in
          einem neuen Tab. Sie steht ganz am Ende und nicht in der Karte: ein
          Fenster gehört der Seite und nicht dem Kasten, aus dem es aufging. */}
      {dateifenster}
    </>
  );
}


/**
 * Wiedervorlagen an einem Auftrag.
 *
 * EIN LAUF, DER IMMER DASSELBE MELDET, IST EINE BEHAUPTUNG. Der Lauf
 * „Fällige Erinnerungen" sieht seit Migration 022 jeden Morgen nach und
 * meldete jeden Morgen „Keine Erinnerung fällig" - nicht weil keine fällig
 * war, sondern weil es keinen Weg gab, eine anzulegen.
 *
 * Sie ist Innendienstsache. Was der Sub tun soll, steht im Gewerkekommentar;
 * hier steht, was WIR noch klären wollen.
 */
function Wiedervorlagen(
  { zeilen, darf: darfIch, darfGbgs, auftragId, laden }:
  { zeilen: any[]; darf: boolean; darfGbgs?: boolean; auftragId: string; laden: () => Promise<void> }
) {
  const [meldung, setMeldung] = useState('');
  const [offenDialog, setOffenDialog] = useState(false);
  const [text, setText] = useState('');
  const [wann, setWann] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  const offene = zeilen.filter((e) => !e.erledigt_am);
  const erledigte = zeilen.filter((e) => e.erledigt_am);
  const heute = heuteIso();

  async function tu(was: () => Promise<any>) {
    setLaeuft(true); setFehler(null);
    try { await was(); await laden(); return true; }
    catch (f) { setFehler(ausFehler(f)); return false; }
    finally { setLaeuft(false); }
  }

  return (
    <div className="karte">
      <h3>Wiedervorlage</h3>
      <div className="leib">
        <Fehlerkasten lage={fehler} />
        {meldung && <div className="hinweis klein" style={{ marginBottom: 8 }}>{meldung}</div>}
        {!zeilen.length ? (
          <p className="klein leise" style={{ marginTop: 0 }}>
            Nichts vorgemerkt. Was hier steht, meldet die Automatik am fälligen
            Tag an den Innendienst.
          </p>
        ) : (
          <table className="liste">
            <thead>
              <tr>
                <th style={{ width: 110 }}>Fällig</th>
                <th>Woran</th>
                <th style={{ width: 120 }}>Angelegt von</th>
                {darfIch && <th style={{ width: 110 }}></th>}
              </tr>
            </thead>
            <tbody>
              {[...offene, ...erledigte].map((e: any) => (
                <tr key={e.id} style={{ cursor: 'default' }}>
                  <td className="klein">
                    {datum(e.faellig_am)}
                    {e.uhrzeit && (
                      <div className="leise">{String(e.uhrzeit).slice(0, 5)}</div>
                    )}
                    {/* ÜBERFÄLLIG IST NICHT DASSELBE WIE FÄLLIG. Eine
                        Wiedervorlage, die seit drei Wochen offen steht, ist
                        die, wegen der man diese Karte aufmacht. */}
                    {!e.erledigt_am && e.faellig_am <= heute && (
                      <div style={{ color: 'var(--ueberfaellig)' }}>fällig</div>
                    )}
                  </td>
                  <td className={e.erledigt_am ? 'klein leise' : 'klein'}>
                    {e.text}
                    {e.erledigt_am && (
                      <div className="leise">
                        erledigt {datum(e.erledigt_am)}
                      </div>
                    )}
                    {!e.erledigt_am && e.gemeldet_am && (
                      <div className="leise">
                        gemeldet {datum(e.gemeldet_am)}
                      </div>
                    )}
                  </td>
                  <td className="klein leise">{e.angelegt_von ?? ''}</td>
                  {darfIch && (
                    <td className="klein">
                      <button className="knopf klein" disabled={laeuft}
                              onClick={() => tu(() =>
                                api.erinnerungAbhaken(e.id, !e.erledigt_am))}>
                        {e.erledigt_am ? 'Wieder öffnen' : 'Erledigt'}
                      </button>
                      {/* BLEIBT BESTEHEN (.462): eine Frage, ein Klick, der
                          Roboter verlängert im GBGS so weit wie möglich. */}
                      {!e.erledigt_am && darfGbgs && (e.gbgs_bauaufgabe_bid || e.gbgs_gebaeudeauftrag_id) && (
                        e.verlaengerung_seit
                          ? <div className="leise" style={{ marginTop: 4 }}>Verlängerung läuft</div>
                          : <button className="knopf klein" style={{ marginTop: 4 }} disabled={laeuft}
                                    onClick={async () => {
                                      if (!await fragen('Ist der Auftrag noch offen und bleibt der Wartegrund bestehen?\n\nJa: der Roboter verlängert die Wiedervorlage im GBGS so weit wie möglich und trägt dasselbe Datum hier ein.')) return;
                                      void tu(async () => { const r = await api.gbgsWartegrundVerlaengern({ erinnerung_id: e.id }); setMeldung(r?.klartext ?? ''); });
                                    }}>
                              Bleibt bestehen, verlängern
                            </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {darfIch && (
          <div className="reihe" style={{ marginTop: 10 }}>
            <button className="knopf" disabled={laeuft}
                    onClick={() => { setText(''); setWann(''); setFehler(null);
                                     setOffenDialog(true); }}>
              Wiedervorlage anlegen
            </button>
          </div>
        )}
      </div>

      {offenDialog && (
        <Dialog titel="Wiedervorlage anlegen" laeuft={laeuft}
                zu={() => setOffenDialog(false)} breite={520}>
          <Fehlerkasten lage={fehler} />
          <Text name="faellig_am" titel="Fällig am" pflicht art="date"
                wert={wann} setze={setWann} fehler={fehler}
                hinweis={'Ein Samstag bleibt ein Samstag: es wird nicht auf '
                       + 'den nächsten Werktag geschoben. Wer den Samstag '
                       + 'meint, meint ihn.'} />
          <Text name="text" titel="Woran soll erinnert werden" pflicht
                mehrzeilig wert={text} setze={setText} fehler={fehler}
                hinweis={'Ein Datum ohne Satz ist am fälligen Tag keine Hilfe: '
                       + 'dann steht dort eine Adresse und niemand weiß, '
                       + 'warum.'} />
          <div className="reihe" style={{ marginTop: 12 }}>
            <button className="knopf haupt" disabled={laeuft}
                    onClick={async () => {
                      const ok = await tu(() => api.erinnerungAnlegen(
                        auftragId, { faellig_am: wann, text }));
                      if (ok) setOffenDialog(false);
                    }}>
              {laeuft ? 'Einen Moment' : 'Anlegen'}
            </button>
            <button className="knopf" disabled={laeuft}
                    onClick={() => setOffenDialog(false)}>Abbrechen</button>
          </div>
        </Dialog>
      )}
    </div>
  );
}


/**
 * Was an diesem Auftrag noch fehlt, bevor er fertig gemeldet werden kann.
 *
 * ORIGINALTEXT MARVIN (26.08.2026)
 *   „Wie ist die auftragsverwaltung, Seite/funktionen, Upload ... für die
 *    subs, ist das alles für die gebaut?"
 *
 * DER WEG STAND DA UND WURDE VON NIEMANDEM GERUFEN. `GET /auftraege/:id/
 * fertigmeldung` rechnet die fehlenden Pflichtordner aus und gibt einen
 * fertigen Satz dazu zurück; das Recht dafür (`akte.lesen`) hat jeder Sub.
 * Gerufen hat es nur der Dialog „Fertig melden" - und den hatte der Sub bis
 * zum 26.08.2026 gar nicht, weil die Werkzeugleiste an `auftrag.schreiben`
 * hing. Der Monteur konnte also nirgends nachsehen, was von ihm noch erwartet
 * wird. Er hat Fotos hochgeladen und gehofft.
 *
 * Sie steht GANZ OBEN in seiner Akte, vor den Gewerken: es ist die einzige
 * Frage, mit der er den Bildschirm aufmacht.
 */
function WasFehlt({ auftragId, gewerke, zumUpload, zumAufmass }:
                  { auftragId: string; gewerke: any[];
                    zumUpload: (() => void) | null;
                    zumAufmass: (() => void) | null }) {
  const [d, setD] = useState<any>(null);
  const gemeint = useRef(auftragId);
  gemeint.current = auftragId;
  /* AUCH DIESE KARTE HAT IHREN EIGENEN ABRUF, also braucht sie ihren eigenen
     Nachzug: „was noch fehlt" ändert sich, sobald jemand eine Datei ablegt -
     und das geschieht auf einem Reiter daneben. */
  async function laden() {
    const fuer = auftragId;
    try {
      const r = await api.fertigmeldung(fuer);
      if (gemeint.current === fuer) setD(r);
    } catch { if (gemeint.current === fuer) setD(null); }
  }
  useEffect(() => { laden(); }, [auftragId]);
  useFrisch(laden);
  if (!d) return null;

  /* NUR, WAS ER SELBST FÜLLEN KANN.
     `hinweis` und `fehlend` aus `GET /fertigmeldung` zählen ALLE Bereiche:
     der Tiefbauer las hier „Montage/04 NVt" und konnte nichts damit anfangen.
     Eine Aufgabenliste mit fremden Aufgaben darin ist keine Aufgabenliste. */
  const meine = meineOrdner<any>(d.ordner, meinBereich(true, gewerke));
  const offen = meine.filter((o) => o.pflicht && !o.dateien).map((o) => o.pfad);
  const fremd = (d.fehlend ?? []).filter((p: string) => !offen.includes(p));

  return (
    <div className="karte">
      <h3>
        Was noch fehlt
        {/* DER WEG DAHIN, ALS CHIP.
            ORIGINALTEXT MARVIN (01.09.2026)
              „und oben beim „was noch fehlt" kannst du oben noch den chip
               machen zum verweis für den upload"
            Die Karte sagte, WAS fehlt, und nicht, wo es hingehört. Der Monteur
            las „Es fehlen noch 4 Pflichtordner", suchte dann den Reiter
            „Dateien" und darin den richtigen Ordner. **Eine Liste von Aufgaben
            ohne den Weg zur Erledigung ist eine Liste von Vorwürfen.** */}
        <Kartenweg hin={zumUpload} text="Dateien hochladen" />
        {/* DAS AUFMASS IST DIE ZWEITE HÄLFTE DERSELBEN AUFGABE: Nachweise
            hochladen und Mengen eintragen. Es gehört an dieselbe Karte. */}
        <Kartenweg hin={zumAufmass} text="Aufmaß eintragen" />
      </h3>
      <div className="leib">
        {offen.length === 0 ? (
          <p className="klein" style={{ margin: 0 }}>
            {fremd.length === 0
              ? 'Alle Pflichtordner sind gefüllt. Fertig melden geht über '
                + '„Werkzeuge" oben im Kopf.'
              : 'Bei dir ist alles vollständig. Der Auftrag wartet noch auf '
                + 'Nachweise aus einem anderen Gewerk.'}
          </p>
        ) : (
          <>
            <p className="klein" style={{ marginTop: 0 }}>
              {offen.length === 1
                ? 'Es fehlt noch ein Pflichtordner:'
                : `Es fehlen noch ${offen.length} Pflichtordner:`}
            </p>
            <ul className="klein" style={{ margin: 0, paddingLeft: 18 }}>
              {offen.map((o: string) => <li key={o}>{o}</li>)}
            </ul>
          </>
        )}
        {(d.freistellung ?? []).length > 0 && (
          <p className="klein leise" style={{ marginBottom: 0, marginTop: 10 }}>
            Freigestellt: {d.freistellung.join(', ')}.
          </p>
        )}
      </div>
    </div>
  );
}


/**
 * Einen Eintrag der Auftragsinfo berichtigen.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „bei der sauftragsinfo kann ich nur einen eintrag schreiben, aber nichts
 *    editieren."
 *
 * DER VERFASSER UND DER ZEITPUNKT BLEIBEN STEHEN, und sie stehen sichtbar im
 * Dialog. Was sich ändert, ist der Wortlaut - und danach steht in der Liste,
 * dass berichtigt wurde, von wem und wann. Ein Eintrag, der sich unbemerkt
 * ändern lässt, ist keine Spur mehr, sondern eine Behauptung.
 */
function EintragBerichtigen({
  e, auftragId, zu, fertig,
}: {
  e: any; auftragId: string; zu: () => void; fertig: () => Promise<void>;
}) {
  const [text, setText] = useState(String(e.text ?? ''));
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  return (
    <Dialog titel="Eintrag berichtigen" breite={620} laeuft={laeuft} zu={zu}
            untertitel={`${zeitpunkt(e.zeitpunkt)}, ${e.verfasser_name ?? 'ohne Namen'}`}>
      <Fehlerkasten lage={fehler} />
      <p className="klein leise" style={{ marginTop: 0 }}>
        Verfasser und Zeitpunkt bleiben, wie sie sind. Nach dem Speichern steht
        in der Liste, dass dieser Eintrag berichtigt wurde - mit Ihrem Namen
        und dem heutigen Datum.
      </p>
      <Text name="text" titel="Text" mehrzeilig wert={text} setze={setText}
            fehler={fehler} />
      <div className="knopfreihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt"
                disabled={laeuft || !text.trim() || text === e.text}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try {
                    await api.auftragInfoAendern(auftragId, e.id, text.trim());
                    await fertig();
                  } catch (f) { setFehler(ausFehler(f)); }
                  finally { setLaeuft(false); }
                }}>
          Speichern
        </button>
        <button className="knopf" onClick={zu}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


/**
 * Einen Eintrag der Auftragsinfo löschen.
 *
 * ORIGINALTEXT MARVIN (01.09.2026)
 *   „auch soll die möglichkeit sein, jeder seinen eigenen eintrag zu löschen
 *    und der innendienst alle zu löschen und bearbeiten zu können in der
 *    Auftragsinfo."
 *
 * EINE RÜCKFRAGE, DIE DEN SATZ ZEIGT. Ein „Wirklich löschen?" ohne den
 * Wortlaut daneben ist eine Frage, die man nicht beantworten kann - in einer
 * Liste mit zwanzig Einträgen weiss danach niemand, welchen er gerade
 * entfernt. Und sie sagt hin, dass die Zeile bestehen bleibt: das ist keine
 * Beruhigung, sondern eine Auskunft über das, was gleich passiert.
 */
function EintragLoeschen({
  e, auftragId, zu, fertig,
}: {
  e: any; auftragId: string; zu: () => void; fertig: () => Promise<void>;
}) {
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  return (
    <Dialog titel="Eintrag löschen" breite={560} laeuft={laeuft} zu={zu}
            untertitel={`${zeitpunkt(e.zeitpunkt)}, ${e.verfasser_name ?? 'ohne Namen'}`}>
      <Fehlerkasten lage={fehler} />
      <p style={{ marginTop: 0 }}><b>{e.text}</b></p>
      <p className="klein leise">
        Der Eintrag verschwindet aus der Auftragsinfo und aus der Zählung in
        der Arbeitsliste. Die Zeile selbst bleibt mit Ihrem Namen und dem
        heutigen Datum bestehen, und der Wortlaut steht im Protokoll.
      </p>
      <div className="knopfreihe" style={{ marginTop: 12 }}>
        <button className="knopf haupt" disabled={laeuft}
                onClick={async () => {
                  setLaeuft(true); setFehler(null);
                  try {
                    await api.auftragInfoLoeschen(auftragId, e.id);
                    await fertig();
                  } catch (f) { setFehler(ausFehler(f)); }
                  finally { setLaeuft(false); }
                }}>
          Löschen
        </button>
        <button className="knopf" onClick={zu}>Abbrechen</button>
      </div>
    </Dialog>
  );
}


/**
 * WAS DER TIEFBAU HINTERLASSEN HAT.
 *
 * ORIGINALTEXT MARVIN (31.08.2026)
 *   „die können angezeigt bekommen, wann der termin war, bzw das er erledigt
 *    ist. ob der tiefbau von uns war oder die pipe schon im haus, wieviel
 *    meter pipe verlegt wurden, das wars auch. die infos können dann auch was
 *    auffälliger sein, was der tiefbau einträgt bzw das kein tiefbau erfolgt
 *    ist durch uns. weil das suchen die monteuere später für die checkliste."
 *
 * VIER ANGABEN, DIE AN VIER STELLEN STANDEN. Der Termin an der Gewerkemarke,
 * „kein Tiefbau" in einem Haken des Innendienstes, die Pipe im Block
 * „Technik" (den ein Monteur nicht immer hat) und die Meter im Aufmass, das
 * ihm nicht gehört. Wer sie für seine Checkliste sucht, sucht sie viermal.
 *
 * DIE WICHTIGSTE AUSKUNFT IST EINE ABWESENHEIT: **es wurde gar nicht
 * gegraben.** Dann steht sie oben und farbig, denn danach richtet sich, was
 * der Monteur einpackt. Eine Zeile, die dasselbe blass sagt wie die drei
 * daneben, wird nicht gelesen.
 *
 * ZWEI METERZAHLEN, UND JEDE SAGT, WOHER SIE KOMMT. Die eine steht in der
 * Bauakte, die andere hat der Tiefbau aufgemessen. Sie können auseinander
 * gehen - und dann ist genau das die Auskunft. Eine einzige Zahl aus beiden
 * zu machen hiesse, eine davon zu verschweigen.
 *
 * UND SEIT DEM 08.09.2026 STEHT HIER, WAS DER TIEFBAU WIRKLICH VORGEFUNDEN
 * HAT.
 *
 * ORIGINALTEXT MARVIN
 *   „hier geht es um die rückmeldungen die der tiefbau draußen macht und
 *    notwendig sind, dass er sie bei sich im auftrag einträgt und anpasst,
 *    diese infos sollen dann ja für alle anderen sichtbar sein"
 *
 * Auf die Rückfrage, welche Werte gemeint sind:
 * > „er korigiert die länge des SNR, rohrfarbe, Verband und rohrstärke, alle
 * >  diese werte stammen von der planung, können bzw werden vor ort aber
 * >  abweichen und wir brauchen alle (Tiefbau, Monteure, innendienst) die
 * >  richtigen angaben, daher ist es essentiell."
 *
 * **DER ISTWERT FÜHRT, DER PLANWERT BLEIBT DANEBEN STEHEN** - und nur dann,
 * wenn er etwas anderes sagt. Ein Planwert, der neben einem gleichlautenden
 * Istwert steht, ist keine zweite Auskunft, sondern eine Zeile, die man beim
 * dritten Mal überliest. Gibt es keine Rückmeldung, steht der Planwert da und
 * sagt, dass er einer ist: **ein Wert ohne seine Herkunft ist auf einer
 * Baustelle eine Vermutung.**
 */
function Tiefbaustand(
  { x, melden, nichtErledigt, fertigMelden, baustelleFertig }: {
    x: any;
    melden: (() => void) | null;
    nichtErledigt: (() => void) | null;
    fertigMelden: (() => void) | null;
    baustelleFertig: (() => void) | null;
  }
) {
  if (!x) return null;
  const p = x.pipe ?? {};

  /** Eine Zeile der Pipe. Leer heisst: es gibt dazu gar nichts zu sagen. */
  const zeile = (titel: string, w: any, einheit = '') => {
    const ist = w?.ist;
    const plan = w?.plan;
    const leer = (v: any) =>
      v === null || v === undefined || String(v).trim() === '';
    if (leer(ist) && leer(plan)) return null;
    const zahl = (v: any) => (einheit && typeof v === 'number'
      ? `${menge(v)}${einheit}` : `${v}${einheit}`);
    return (
      <>
        <dt>{titel}</dt>
        <dd>
          {leer(ist) ? (
            <>
              {zahl(plan)}{' '}
              <span className="leise klein">laut Planung</span>
            </>
          ) : (
            <>
              <b>{zahl(ist)}</b>
              {!leer(plan) && String(plan) !== String(ist) && (
                <span className="leise klein"> (Planung: {zahl(plan)})</span>
              )}
            </>
          )}
        </dd>
      </>
    );
  };

  const hatPipe = [p.laenge, p.verband, p.roehrchen, p.breite].some(
    (w: any) => w && (w.ist !== null && w.ist !== undefined && String(w.ist).trim() !== ''
                      || w.plan !== null && w.plan !== undefined
                         && String(w.plan).trim() !== ''));

  /* KEIN TIEFBAU HAT ZWEI URSACHEN, und sie sind nicht dasselbe: der
     Auftragstyp sieht gar keinen vor (eine Vorgabe der Telekom), ODER jemand
     hat auf der Baustelle festgestellt, dass keiner nötig war (ein Befund).
     Der Monteur braucht beide, und wer sie zusammenzieht, nimmt ihm den
     Unterschied. */
  const ohneTiefbau = !x.unser_gewerk || x.kein_tiefbau || x.pipe_war_da;

  const grund = !x.unser_gewerk
    ? 'Der Auftragstyp sieht bei uns keinen Tiefbau vor.'
    : x.pipe_war_da
      ? 'Der Tiefbau hat gemeldet, dass die Pipe schon im Haus lag.'
      : x.kein_tiefbau
        ? 'Es ist eingetragen, dass kein Tiefbau nötig war.'
        : null;

  return (
    <div className="karte">
      <h3>
        Tiefbau
        <Kartenweg hin={melden} text="Zurückmelden" />
        <Kartenweg hin={baustelleFertig} text="Baustelle fertig" />
        <Kartenweg hin={fertigMelden} text="Fertig melden" />
        <Kartenweg hin={nichtErledigt} text="Nicht erledigt" />
      </h3>
      <div className="leib">
        {/* DIE DRUCKLUFTPRÜFUNG STEHT GANZ OBEN, denn sie ist die Antwort auf
            die Frage, mit der die Montage auf diese Karte sieht: kann ich
            morgen einblasen. `null` heisst „nicht geprüft" - und das ist eine
            dritte Auskunft, nicht „nein". */}
        {x.druckluft_geprueft && (
          <div className={'hinweis' + (x.druckluft_dicht ? '' : ' warnung')}
               style={{ marginTop: 0 }}>
            <b>Druckluftprüfung:</b>{' '}
            {x.druckluft_dicht
              ? 'die Pipe ist durchgängig.'
              : 'die Pipe ist NICHT durchgängig. So darf die Montage nicht anfahren.'}
            {x.druckluft_name && (
              <span className="leise klein"> Gemeldet von {x.druckluft_name}.</span>
            )}
          </div>
        )}
        {ohneTiefbau ? (
          <div className="hinweis warnung" style={{ marginTop: 0 }}>
            <b>Hier wurde von uns nicht gegraben.</b>
            {grund && <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
              {grund}
            </div>}
          </div>
        ) : x.erledigt ? (
          <div className="hinweis erledigt" style={{ marginTop: 0 }}>
            <b>Der Tiefbau ist fertig{x.erledigt_am
              ? ` seit ${datum(x.erledigt_am)}` : ''}.</b>
            {x.restarbeiten_offen && (
              <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
                Es sind allerdings noch Restarbeiten offen.
              </div>
            )}
          </div>
        ) : x.termin ? (
          <div className="hinweis" style={{ marginTop: 0 }}>
            <b>Tiefbau am {datum(x.termin)}
              {x.von ? `, ${String(x.von).slice(0, 5)} Uhr` : ''}.</b>
            <div className="klein" style={{ fontWeight: 400, marginTop: 4 }}>
              Noch nicht als erledigt gemeldet.
            </div>
          </div>
        ) : (
          <div className="hinweis warnung" style={{ marginTop: 0 }}>
            <b>Für den Tiefbau gibt es noch keinen Termin.</b>
          </div>
        )}

        {/* NUR DIE GEBAUTE LÄNGE, UND DAS IST EINE RÜCKNAHME.
            ORIGINALTEXT MARVIN (01.09.2026)
              „die länge laut bauakte ist uninteressant"
            Hier standen ZWEI Meterzahlen nebeneinander, mit dem Satz „jede
            sagt, woher sie kommt". Der Gedanke war richtig und die Auswahl
            falsch: die Zahl der Bauakte ist eine PLANUNGSANGABE der Telekom,
            und diese Karte beantwortet dem Monteur eine Frage über die
            BAUSTELLE - wie viel wirklich liegt. **Zwei Zahlen, von denen eine
            nichts über den Zustand vor Ort sagt, sind keine zwei Auskünfte,
            sondern eine Auskunft und eine Verwechslungsgefahr.** Die
            Planungsangabe steht weiter im Block „Technik", wo sie hingehört,
            und heisst dort auch so. */}
        <dl className="felder" style={{ marginTop: 10 }}>
          {/* ZWEI LÄNGEN, UND JEDE TRÄGT DEN NAMEN IHRER POSITION.
              Hier stand EINE Zeile „vom Tiefbau aufgemessen", und sie stand
              direkt über „Was an der Pipe liegt → SNR-Länge": zwei Zahlen
              über dieselbe Baustelle, mit verschiedenen Werten und ohne einen
              Satz dazu, welche welche ist. Das ist Marvins „die daten werden
              doppelt geschrieben, mit unterschiedlichen werten", und es war
              keine Anzeige, sondern eine Rechnung: die alte Zahl hat Graben,
              Oberfläche und Mehrtiefe addiert und die Pipe weggelassen.
              **Der Graben ist das Loch, die Pipe ist, was darin liegt** - sie
              stehen nebeneinander und werden nie addiert. */}
          {x.graben_aufgemessen != null && (
            <>
              <dt>Graben aufgemessen</dt>
              <dd>{menge(x.graben_aufgemessen)} m</dd>
            </>
          )}
          {x.pipe_aufgemessen != null && (
            <>
              <dt>Pipe aufgemessen</dt>
              <dd>{menge(x.pipe_aufgemessen)} m</dd>
            </>
          )}
          {x.kabel_im_haus && (<><dt>Kabel</dt><dd>liegt schon im Haus</dd></>)}
          {x.kein_oeffentl_grund && (
            <><dt>Grund</dt><dd>kein öffentlicher Grund berührt</dd></>
          )}
        </dl>

        {/* WAS AN DER PIPE WIRKLICH LIEGT.
            Die vier Angaben stammen aus der Planung und werden draussen
            berichtigt. Sie stehen hier und nicht nur im Block „Technik":
            **der Monteur, der danach kommt, hat diesen Block nicht immer** -
            und er braucht Farbe, Verband und Stärke, bevor er einbläst. */}
        {hatPipe && (
          <>
            <div className="klein leise" style={{ marginTop: 12 }}>
              Was an der Pipe liegt
            </div>
            <dl className="felder" style={{ marginTop: 4 }}>
              {zeile('SNR-Länge', p.laenge, ' m')}
              {zeile('Rohrverband', p.verband)}
              {zeile('Röhrchen', p.roehrchen)}
              {zeile('Rohrstärke', p.breite)}
            </dl>
            {p.gemeldet_am && (
              <p className="klein leise" style={{ marginTop: 4 }}>
                Zuletzt von der Baustelle zurückgemeldet
                {' '}{zeitpunkt(p.gemeldet_am)}
                {p.gemeldet_von ? ` von ${p.gemeldet_von}` : ''}.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * DIE POST EINES AUFTRAGS - MIT EINER KLAMMER AN DEN ZEILEN, DIE ETWAS TRAGEN.
 *
 *   ORIGINALTEXT MARVIN (08.09.2026, Punkt 7 der offenen Liste)
 *     „Postfach: Design, Thumbnails der Anhänge, Anhänge in einem Fenster
 *      statt neuem Tab, Büroklammer statt Mailsymbol am Auftrag, verschobene
 *      Popups"
 *
 * EIN UMSCHLAG SAGT „MAIL", EINE KLAMMER SAGT „ES HÄNGT ETWAS DARAN" - und in
 * einer Liste, in der JEDE Zeile eine Mail ist, sagt der Umschlag nichts. Sie
 * steht deshalb dort, wo es wirklich einen Unterschied gibt: an einer Zeile
 * mit Anhängen, und an keiner ohne. Die ZAHL bleibt daneben; ein Zeichen sagt
 * „es hängt etwas daran" und nicht, wie viel.
 *
 * SIE LÄDT SELBST, UND ZWAR ERST BEIM ZEICHNEN. Die Akte holt ihre Blöcke in
 * EINER Antwort; die Post daran anzuhängen hiesse, sie bei jedem Öffnen der
 * Akte mitzuschleppen - für jeden Auftrag, auch für die, an denen keine liegt.
 *
 * DREI AUSGÄNGE UND NICHT ZWEI:
 *
 *   noch nicht da   gar nichts. Die Akte trägt ein Dutzend Karten; eine, die
 *                   eine Sekunde lang aufblitzt, ist Lärm.
 *   nichts da       gar nichts. **Eine Karte, die fast immer leer ist, wird
 *                   nach zwei Wochen nicht mehr gelesen** - und dann fehlt sie
 *                   an dem Tag, an dem etwas darin steht.
 *   ausgefallen     die Karte MIT dem Grund. **Ein Weg, der nicht antwortet,
 *                   sieht sonst aus wie ein Auftrag ohne Post**, und das sind
 *                   zwei ganz verschiedene Auskünfte.
 *
 * UND ES GIBT KEINEN KLICK. Einen gebauten Weg von hier zu EINER bestimmten
 * Mail im Postfach gibt es nicht; einen zu erfinden hiesse, eine Zeile
 * anklickbar zu machen, die danach nirgends hinführt - **ein Knopf, der auf
 * nichts führt, ist schlimmer als kein Knopf.** Wo die Post steht, sagt der
 * Satz darunter.
 */
function PostAmAuftrag({ auftragId }: { auftragId: string }) {
  const [zeilen, setZeilen] = useState<any[] | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);

  const gemeint = useRef(auftragId);
  gemeint.current = auftragId;
  /* UND AUCH DIESE. Wer eine Mail an den Auftrag zieht, ändert diese Liste -
     und tut es in einem Fenster, das die Karte nicht kennt. */
  async function laden() {
    const fuer = auftragId;
    try {
      const r: any = await api.postfachAmAuftrag(fuer);
      if (gemeint.current === fuer) { setZeilen(r ?? []); setFehler(null); }
    } catch (f) { if (gemeint.current === fuer) setFehler(ausFehler(f)); }
  }
  useEffect(() => { setZeilen(null); setFehler(null); laden(); }, [auftragId]);
  useFrisch(laden);

  if (fehler) {
    return (
      <div className="karte">
        <h3>Post</h3>
        <div className="leib"><Fehlerkasten lage={fehler} /></div>
      </div>
    );
  }
  if (!zeilen || zeilen.length === 0) return null;

  return (
    <div className="karte">
      <h3>Post ({zeilen.length})</h3>
      <div className="leib">
        <table className="liste">
          <thead>
            <tr>
              <th>Wann</th><th>Richtung</th><th>Wer</th><th>Betreff</th>
            </tr>
          </thead>
          <tbody>
            {zeilen.map((z: any) => (
              <tr key={z.id}>
                <td className="klein nichtumbrechen">{zeitpunkt(z.zeitpunkt)}</td>
                <td>
                  <span className={'merker '
                        + (z.richtung === 'aus' ? 'warnung' : 'gruen')}>
                    {z.richtung === 'aus' ? 'raus' : 'rein'}
                  </span>
                </td>
                <td className="klein">
                  {z.richtung === 'aus'
                    ? (z.empfaenger ?? []).join(', ')
                    : (z.absender_name || z.absender || 'ohne Absender')}
                </td>
                <td>
                  {z.betreff || <span className="leise">ohne Betreff</span>}
                  {/* HIER STEHT DIE KLAMMER, UND NUR HIER. Eine Zeile ohne
                      Anhang bekommt kein Zeichen - sonst trüge es jede, und
                      dann sagt es nichts mehr. */}
                  {z.anhaenge > 0 && (
                    <>, <Klammer titel={z.anhaenge === 1
                            ? '1 Anhang' : `${z.anhaenge} Anhänge`} />
                      {' '}{z.anhaenge}</>
                  )}
                  {z.erfolgreich === false && (
                    <div className="klein warnung">nicht hinausgegangen</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="klein leise" style={{ marginBottom: 0 }}>
          Der ganze Schriftverkehr steht unter „Postfach". Hier stehen die
          100 jüngsten Zeilen zu dieser Baustelle.
        </p>
      </div>
    </div>
  );
}

/** Eine Mailadresse bricht, wenn überhaupt, hinter dem @ und nicht mitten im
 *  Wort (.233, Marvin am Tablet: „vwz@verwaltungszentrum.co" / „m"). */
function mailMitUmbruch(mail: string) {
  const i = String(mail ?? '').indexOf('@');
  if (i < 0) return mail;
  return <>{mail.slice(0, i + 1)}<wbr />{mail.slice(i + 1)}</>;
}

/* AAZ UND VAO HÄNGEN (.466). ORIGINALTEXT MARVIN (23.10.2026, Laibachstr. 9A):
   „es fehlt eigentlich jetzt da noch ein Knopf von wegen nochmal anstoßen zu
   versenden, weil wie kriege ich das Ganze jetzt sonst aus dem System raus?"
   Steht als letzter Vermerk der Behördenpost „nicht versendet", zeigt die
   Akte den Grund oben und den Knopf. Er baut Papiere mit offenen
   Platzhaltern neu und sendet über denselben Weg wie nach der Terminvergabe. */
function BehoerdenpostHaengt({ x, auftragId, laden }: { x: { text: string; zeitpunkt: string }; auftragId: string; laden: () => Promise<void> }) {
  const [laeuft, setLaeuft] = useState(false);
  const [antwort, setAntwort] = useState<string | null>(null);
  const [fehler, setFehler] = useState<Fehlerlage | null>(null);
  return (
    <div className="karte" style={{ borderColor: 'var(--ueberfaellig)' }}>
      <h3>Behördenpost nicht versendet</h3>
      <div className="leib">
        <Fehlerkasten lage={fehler} />
        <p className="klein" style={{ marginTop: 0 }}>{x.text}</p>
        <p className="klein leise">Stand {datum(x.zeitpunkt)}</p>
        {antwort && <div className="hinweis klein">{antwort}</div>}
        <button className="knopf haupt" disabled={laeuft}
                onClick={async () => {
                  setLaeuft(true); setFehler(null); setAntwort(null);
                  try { const r = await api.behoerdenpostAnstossen(auftragId); setAntwort(r?.klartext || 'Angestoßen.'); await laden(); }
                  catch (f) { setFehler(ausFehler(f)); }
                  finally { setLaeuft(false); }
                }}>
          {laeuft ? 'Einen Moment' : 'Papiere neu erzeugen und noch einmal senden'}
        </button>
      </div>
    </div>
  );
}
